/**
 * Domain types shared by every app.
 *
 * Hand-written rather than generated, for now. The spec calls for types
 * generated from the OpenAPI document, and that is the right end state — but a
 * generator wired up before any screen exists produces a large surface nobody
 * has used, and the enums below are the part that must not drift. When the
 * client generator lands, these become the hand-maintained remainder.
 */

/**
 * The product name and logo, for display. The one copy lives in the API
 * (which cannot import this package) and every app reads it from here.
 */
export { BRAND, type Brand } from '../../../apps/api/src/shared/brand';
import { BRAND } from '../../../apps/api/src/shared/brand';

/** Preserve historical outcomes and represent pending evaluation explicitly. */
export type FitnessDecision =
  | 'FIT' | 'FIT_WITH_RESTRICTION' | 'TEMPORARILY_UNFIT' | 'UNFIT' | 'FURTHER_EVALUATION';

export const FITNESS_LABEL: Record<FitnessDecision, string> = {
  FIT: 'Fit for duty',
  FIT_WITH_RESTRICTION: 'Fit with restrictions',
  TEMPORARILY_UNFIT: 'Temporarily unfit',
  UNFIT: 'Unfit for duty',
  FURTHER_EVALUATION: 'Further evaluation required',
};

export type ExamStatus =
  | 'DRAFT' | 'REQUESTED' | 'ROUTED' | 'CONSENT_PENDING' | 'SCHEDULED'
  | 'NO_SHOW_OPEN' | 'NO_SHOW_CLOSED' | 'CHECKED_IN' | 'IN_PROGRESS'
  | 'LAB_PENDING' | 'REVIEW_PENDING' | 'RETEST_REQUIRED' | 'CERTIFIED'
  | 'EXPIRED' | 'CANCELLED';

export const EXAM_STATUS_LABEL: Record<ExamStatus, string> = {
  DRAFT: 'Draft',
  REQUESTED: 'Requested',
  ROUTED: 'Routed to partner',
  CONSENT_PENDING: 'Awaiting consent',
  SCHEDULED: 'Scheduled',
  NO_SHOW_OPEN: 'Missed — needs rebooking',
  NO_SHOW_CLOSED: 'Missed — closed',
  CHECKED_IN: 'Checked in',
  IN_PROGRESS: 'In progress',
  LAB_PENDING: 'Awaiting lab',
  REVIEW_PENDING: 'Awaiting physician',
  RETEST_REQUIRED: 'Retest required',
  CERTIFIED: 'Certified',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

export type CampaignStatus =
  | 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'IN_PROGRESS'
  | 'COMPLETED' | 'CLOSED' | 'CANCELLED';

export type CoverageState =
  | 'COVERED' | 'DUE_SOON' | 'OVERDUE' | 'IN_PROGRESS' | 'NEVER_EXAMINED'
  // Examined, not cleared: the physician's latest decision was not a fit one,
  // so no certificate stands. Distinct from NEVER_EXAMINED on purpose.
  | 'UNFIT' | 'TEMPORARILY_UNFIT' | 'FURTHER_EVALUATION';

export const COVERAGE_LABEL: Record<CoverageState, string> = {
  COVERED: 'Covered',
  DUE_SOON: 'Due soon',
  OVERDUE: 'Overdue',
  IN_PROGRESS: 'In progress',
  NEVER_EXAMINED: 'Never examined',
  UNFIT: 'Unfit',
  FURTHER_EVALUATION: 'Further evaluation required',
  TEMPORARILY_UNFIT: 'Temporarily unfit',
};

// ── API shapes ──────────────────────────────────────────────────

export type SetupMode = 'SELF_SERVICE' | 'SUPPORT_ASSISTED';
export type SetupStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'READY' | 'COMPLETED';

export const SETUP_STATUS_LABEL: Record<SetupStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  READY: 'Ready',
  COMPLETED: 'Completed',
};

export const SETUP_MODE_LABEL: Record<SetupMode, string> = {
  SELF_SERVICE: 'Client self-service',
  SUPPORT_ASSISTED: `${BRAND.name} support-assisted`,
};

/** The banner an impersonated session shows. */
export interface ImpersonationFacts {
  sessionId: string;
  reason: string | null;
  ticketRef: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  endedAt: string | null;
  mode: 'READ_ONLY' | 'FULL';
  actor: { id: string; email: string | null; fullName: string | null };
}

export interface AiSetupSuggestion {
  slug: string;
  industryId?: string | null;
  industryName?: string | null;
  plants: Array<{ code: string; name: string }>;
  departments: Array<{ code: string; name: string }>;
  jobRoles: Array<{ code: string; title: string }>;
}

export interface Principal {
  id: string;
  email: string;
  fullName: string;
  status: string;
  tenant: {
    id: string; name: string;
    status?: string;
    setupMode?: SetupMode;
    setupStatus?: SetupStatus;
    setupCompletedAt?: string | null;
    industryId?: string | null;
    /** The company already has medical cases — past its first day, whatever setup says. */
    operating?: boolean;
  } | null;
  /**
   * The hospital or laboratory this account works for, or null.
   *
   * Mutually exclusive with `tenant`: a venue account belongs to no company,
   * because one venue serves many. It is what tells the console it is a
   * venue's rather than a company's, and it is never both.
   */
  partner: {
    id: string; code: string; name: string;
    type: string; status: string;
  } | null;
  roles: string[];
  /** The same roles by display name, for people to read. Keys are for code. */
  roleNames?: string[];
  permissions: string[];
  scopes: Array<{ scopeType: string; scopeIds: string[] }>;
  impersonated: boolean;
  impersonation?: ImpersonationFacts | null;
}

/**
 * Signing in ends in one of two places, and they are not the same shape.
 *
 * A union rather than one object with optional fields, so a client cannot
 * read `accessToken` off a challenge and get `undefined` where it expected a
 * token — the discriminant forces the caller to handle the second factor
 * rather than crash past it.
 */
export interface MfaChallengeResponse {
  mfaRequired: true;
  /** Grants nothing but the right to present a code. Five minutes. */
  challengeToken: string;
  expiresIn: number;
}

export interface SessionResponse {
  mfaRequired: false;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  /** True when the second factor was satisfied by a recovery code. */
  usedRecoveryCode?: boolean;
  principal: {
    userId: string;
    tenantId: string | null;
    role: string;
    permissions: string[];
  };
}

export type LoginResponse = MfaChallengeResponse | SessionResponse;

/** What the account's security screen shows. Never includes the seed. */
export interface MfaStatus {
  enabled: boolean;
  enrolmentStarted: boolean;
  recoveryCodesRemaining: number;
  recoveryCodesTotal: number;
}

export interface MfaEnrolment {
  /** Base32, shown once so it can be typed into an app that cannot scan. */
  secret: string;
  otpauthUri: string;
  /** A PNG data URL of the same URI, rendered server-side. */
  qrDataUrl: string;
  issuer: string;
  account: string;
}

export interface ComplianceSummary {
  totalEmployees: number;
  covered: number;
  dueSoon: number;
  /** No valid cover and nothing in hand: `lapsed + neverExamined + notCleared`. */
  overdue: number;
  inProgress: number;
  /** Of `inProgress`: the worker did not attend, and the case waits to be rebooked. */
  missedAppointments?: number;
  /** Of `inProgress`: the next move is the company's — rebook, book a slot, raise a retest. */
  openForCompany?: number;
  /** Of `inProgress`: waiting on Operations, the worker, the venue, the lab or the physician. */
  openWaiting?: number;
  /** No certificate, no signed decision, no open case. */
  neverExamined: number;
  /** Held a certificate that ran out, or was withdrawn, and has not been replaced. */
  lapsed: number;
  /** Examined; the physician's latest decision was UNFIT. */
  unfit: number;
  /** Examined; the latest decision was TEMPORARILY_UNFIT and a retest is owed. */
  temporarilyUnfit: number;
  furtherEvaluation?: number;
  /** `unfit + temporarilyUnfit` — examined, not cleared. */
  notCleared: number;
  coveragePercent: number;
  asOf: string;
  /** This month so far, and the same number of days at the start of last month. */
  thisMonth?: MonthActivity;
  lastMonthSameDays?: MonthActivity;
}

export interface MonthActivity {
  from: string;
  to: string;
  employeesAdded: number;
  certificatesIssued: number;
  casesRaised: number;
}

export interface OutstandingRow {
  employeeId: string;
  employeeCode: string;
  fullName: string;
  plant: string | null;
  department: string | null;
  jobRole: string | null;
  state: CoverageState;
  action: string;
  certificateNumber: string | null;
  validUntil: string | null;
  /** For a worker not cleared: when the physician signed, and the retest date if one was set. */
  examinedOn?: string | null;
  retestDueOn?: string | null;
  /** The decision a worker not cleared was given, kept when a new case is open over it. */
  notClearedDecision?: 'UNFIT' | 'TEMPORARILY_UNFIT' | 'FURTHER_EVALUATION' | null;
  openOrderId: string | null;
  openOrderStatus?: ExamStatus | null;
  /** The slot a case has booked for today or later, as `YYYY-MM-DD`. */
  bookedFor?: string | null;
  /** Whose move the row is. `YOU` and `HR` are the company's; the rest are waited on. */
  move?: CaseMove;
  needsCompany?: boolean;
}

export type CaseMove = 'YOU' | 'HR' | 'OPERATIONS' | 'WORKER' | 'VENUE' | 'LAB' | 'PHYSICIAN';

/** Who a case is waiting on, for a row that is not the reader's to act on. */
export const CASE_MOVE_LABEL: Record<CaseMove, string> = {
  YOU: 'You', HR: 'Your HR team', OPERATIONS: `${BRAND.name} Operations`, WORKER: 'The employee',
  VENUE: 'The venue', LAB: 'The laboratory', PHYSICIAN: 'The physician',
};

export interface PlantCoverage extends ComplianceSummary {
  plantId: string;
  plantCode: string;
  plantName: string;
}

export interface ActiveRestriction {
  employeeCode: string;
  fullName: string;
  plant: string | null;
  jobRole: string | null;
  restrictionCode: string;
  restriction: string;
  certificateNumber: string | null;
  validUntil: string | null;
}

/**
 * The employee list row.
 *
 * The API returns FOREIGN KEYS, not nested names — the field-level projection
 * selects columns, and a projection that joined three tables to render a list
 * would defeat the point of restricting columns in the first place. Names are
 * resolved client-side from the plant and job-role lookups, which are small,
 * cached, and the same for every row.
 */
export interface EmployeeRow {
  id: string;
  employeeCode: string;
  fullName: string;
  sex?: string | null;
  dateOfBirth?: string | null;
  bloodGroup?: string | null;
  mobile?: string | null;
  status: string;
  plantId?: string | null;
  departmentId?: string | null;
  jobRoleId?: string | null;
}

/** Industrial shift patterns. An enum server-side, so a restriction on night
 *  work is checkable rather than three spellings of the same shift. */
export type WorkShift = 'GENERAL' | 'SHIFT_A' | 'SHIFT_B' | 'SHIFT_C' | 'ROTATING' | 'NIGHT';

export const WORK_SHIFT_LABEL: Record<WorkShift, string> = {
  GENERAL: 'General', SHIFT_A: 'Shift A', SHIFT_B: 'Shift B',
  SHIFT_C: 'Shift C', ROTATING: 'Rotating', NIGHT: 'Night',
};

export type EmploymentStatus =
  | 'CANDIDATE' | 'ACTIVE' | 'ON_LEAVE' | 'TRANSFERRED'
  | 'SUSPENDED' | 'EXITED' | 'NOT_HIRED';

export const EMPLOYMENT_STATUS_LABEL: Record<EmploymentStatus, string> = {
  CANDIDATE: 'Candidate', ACTIVE: 'Active', ON_LEAVE: 'On leave',
  TRANSFERRED: 'Transferred', SUSPENDED: 'Suspended', EXITED: 'Exited',
  NOT_HIRED: 'Not hired',
};

/**
 * Stored employment types. CONTRACT is said "Contractor" everywhere a person
 * reads it (docs/38 D6) — the enum predates the word, and renaming it would be
 * a migration for a label. APPRENTICE is no longer offered for new records
 * but older ones carry it.
 */
export type EmploymentType = 'DIRECT' | 'CONTRACT' | 'APPRENTICE';

export const EMPLOYMENT_TYPE_LABEL: Record<EmploymentType, string> = {
  DIRECT: 'Direct', CONTRACT: 'Contractor', APPRENTICE: 'Apprentice',
};

export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;
export type BloodGroup = (typeof BLOOD_GROUPS)[number];

/**
 * The Administrative projection, in full.
 *
 * Every field here is one the server is willing to give an HR role. There is
 * no clinical content — not because the screen omits it, but because the
 * projection does. Blood group is here since docs/38 D7: it is part of the
 * employee contract HR records at joining, a first-aid fact and not a result.
 */
/** The employee record. The address is the worker's home, in the shared shape. */
export interface EmployeeDetail extends EmployeeRow, Address {
  email?: string | null;
  bloodGroup?: string | null;
  designationId?: string | null;
  contractorId?: string | null;
  employmentType: EmploymentType;
  joinedOn?: string | null;
  exitedOn?: string | null;
  exitReason?: string | null;
  preferredLocale?: string | null;

  workArea?: string | null;
  shift?: WorkShift | null;
  exposureType?: string | null;
  /** Printed on the certificate as "Nature of work". */
  natureOfWork?: string | null;

  contractorEmployeeCode?: string | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;

  /**
   * Whether an identity photo is on file. Never the storage ref: the image
   * comes through a five-minute grant from `GET /employees/:id/photo`.
   * `photoUpdatedAt` changes when it is replaced, so a cached image is not
   * shown for a new one.
   */
  hasPhoto?: boolean;
  photoUpdatedAt?: string | null;

  createdAt: string;
  updatedAt: string;
  version: number;
}

/** A short-lived grant for an employee's photo; `url` is dead in five minutes. */
export interface EmployeePhotoGrant {
  url: string;
  expiresAt: string;
  mimeType: 'image/jpeg' | 'image/png';
  photoUpdatedAt: string | null;
}

export type EmploymentChange =
  | 'CANDIDATE_REGISTERED' | 'HIRED' | 'NOT_HIRED'
  | 'JOINED' | 'TRANSFERRED' | 'ROLE_CHANGED'
  | 'WENT_ON_LEAVE' | 'RETURNED_FROM_LEAVE'
  | 'SUSPENDED' | 'REINSTATED' | 'EXITED';

export const EMPLOYMENT_CHANGE_LABEL: Record<EmploymentChange, string> = {
  CANDIDATE_REGISTERED: 'Candidate registered',
  HIRED: 'Hired',
  NOT_HIRED: 'Not hired',
  JOINED: 'Joined',
  TRANSFERRED: 'Transferred',
  ROLE_CHANGED: 'Role changed',
  WENT_ON_LEAVE: 'Went on leave',
  RETURNED_FROM_LEAVE: 'Returned from leave',
  SUSPENDED: 'Suspended',
  REINSTATED: 'Reinstated',
  EXITED: 'Exited',
};

/** One row of the append-only organisational history. */
export interface EmploymentRecordRow {
  id: string;
  changeType: EmploymentChange;
  effectiveFrom: string;
  effectiveTo?: string | null;
  reason?: string | null;
  createdAt: string;
  plantId: string;
  departmentId?: string | null;
  jobRoleId?: string | null;
}

/** One examination in an employee's history. */
export interface ExaminationRow {
  id: string;
  status: string;
  examinationType: string;
  priority: string;
  dueDate?: string | null;
  employeeId: string;
  plantId: string;
  assignedPartnerId?: string | null;
  assignedPhysicianId?: string | null;
  slaDueAt?: string | null;
  slaBreachedAt?: string | null;
  createdAt: string;
  version: number;
  employee?: { employeeCode: string; fullName: string };
  /** The signed decision on a CERTIFIED case; see `ExaminationOrderRow.outcome`. */
  outcome?: FitnessDecision | null;
}

/**
 * Where a certificate stands TODAY.
 *
 * Not the same as its stored status: a row is ACTIVE until somebody revokes
 * it, and whether it is current, expiring or already lapsed is a question
 * about the date. Computed server-side so the filter and the colour on the
 * screen cannot disagree about what "expiring" means.
 */
export type CertificateExpiryState = 'CURRENT' | 'EXPIRING' | 'EXPIRED' | 'REVOKED' | 'SUPERSEDED';

/** One certificate, as HR is permitted to see it: validity, never findings. */
export interface CertificateRow {
  id: string;
  certificateNumber: string;
  decision: string;
  issuedAt: string;
  validFrom: string;
  validUntil: string;
  status: string;
  generationStatus: string;
  expiryState?: CertificateExpiryState;
  employee?: {
    id: string;
    employeeCode: string;
    fullName: string;
    plantId: string;
  };
}

/** One booked slot, as the appointments worklist returns it. */
export interface AppointmentRow {
  id: string;
  scheduledFor: string;
  slotMinutes: number;
  venueType: string;
  status: string;
  checkedInAt?: string | null;
  cancelReason?: string | null;
  partnerId: string;
  partnerLocationId?: string | null;
  campaignVenueId?: string | null;
  orderId: string;
  version: number;
  partner?: { name: string } | null;
  order?: {
    id: string;
    examinationType: string;
    status: string;
    plantId: string;
    campaignId?: string | null;
    employee?: { id: string; employeeCode: string; fullName: string };
  };
}

/**
 * How a consent was given, in words. The stored value for the worker app is
 * `PWA`, which reached HR and the physician as "Pwa".
 */
export const CONSENT_METHOD_LABEL: Record<string, string> = {
  PWA: 'In the employee app', RECEPTION: 'At the reception desk', PAPER_SCAN: 'On paper, scanned',
};

export const APPOINTMENT_STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Scheduled', CONFIRMED: 'Confirmed', CHECKED_IN: 'Checked in',
  COMPLETED: 'Completed', NO_SHOW: 'No-show', CANCELLED: 'Cancelled',
  RESCHEDULED: 'Rescheduled',
};

/**
 * One address, in the shape the API returns for every entity that has a place.
 *
 * `formattedAddress` and `mapsUrl` are derived server-side rather than in each
 * of the four front ends — they were being rebuilt as
 * `[city, state].filter(Boolean).join(', ')` in three different files, none of
 * which knew about the landmark or the pincode.
 */
export interface Address {
  addressLine1?: string | null;
  addressLine2?: string | null;
  locality?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  postOffice?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  mapsUrl?: string | null;
}

// ── Geo helpers ───────────────────────────────────────────────────

export interface PincodeLookup {
  pincode: string;
  offices: Array<{
    officeName: string; officeType: string | null; district: string; state: string;
    latitude: number | null; longitude: number | null;
  }>;
  state: string | null;
  district: string | null;
  source: 'LOCAL' | 'INDIA_POST' | 'NONE';
}

export interface PlaceSuggestionRow {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetailRow {
  placeId: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  address: Partial<Record<'addressLine1' | 'locality' | 'city' | 'district' | 'state' | 'country' | 'pincode', string>>;
}

/**
 * A company's GST registration, from `GET /geo/gstin/:gstin`. Only the fields
 * GST actually returned are present — nothing is filled in by guesswork.
 */
export interface GstinLookup {
  gstin: string;
  legalName?: string;
  tradeName?: string;
  /** Status at GST ("Active", "Cancelled"…). Not the platform's company status. */
  gstStatus?: string;
  taxpayerType?: string;
  constitution?: string;
  /** As the provider wrote it. */
  registrationDate?: string;
  address: Partial<Record<
    'addressLine1' | 'addressLine2' | 'locality' | 'landmark' | 'city' | 'district' | 'state' | 'pincode',
    string
  >>;
  source: 'gstinapi.in';
  fetchedAt: string;
}

/** Who to call, wherever a contact is recorded. */
export interface ContactDetails {
  contactName?: string | null;
  contactPhone?: string | null;
  contactAltPhone?: string | null;
  contactEmail?: string | null;
}

export interface Plant extends Address {
  id: string;
  code: string;
  name: string;
  status?: string;
}

/** A plant with its own address, contact and what hangs off it. */
export interface PlantDetail extends Plant, ContactDetails {
  version: number;
  departments?: number;
  employees?: number;
}

/**
 * A cursor page. Never an offset: a hospital coordinator paging a worklist
 * while check-ins happen would see rows duplicated and skipped, which in a
 * clinical worklist means somebody tested twice or not at all.
 */
export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  projection?: string;
  /** Every row the query matches, where the endpoint counts them (offset cursors "o:N"). */
  total?: number;
}

/** GET /lab/worklist/counts — exact, over every tube in the caller's venues. */
export interface LabWorklistCounts {
  tubes: {
    total: number; atBench: number; arriving: number; withPathologist: number;
    toReceive: number; inTransit: number; notDrawn: number;
    rejected: number; released: number; sentBack: number; byStatus: Record<string, number>;
  };
  subjects: { toEnter: number; notReceived: number; entered: number };
}

/** GET /lab/results/counts — exact, over every outstanding result in scope. */
export interface LabResultCounts {
  awaitingValidation: number; awaitingRelease: number; outstanding: number;
  criticalOutstanding: number; unacknowledgedCritical: number; subjects: number;
  /** The caller's own entries, waiting for another person to validate them. */
  yourEntriesAwaitingOthers: number;
}

/** A numbered page: what `?page=` returns where an endpoint supports it. */
export interface NumberedPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** RFC 9457. Clients switch on `type`, never on `detail` text. */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  requestId?: string;
  errors?: Array<{ field: string; code: string; message: string }>;
}

// ── CSV import ───────────────────────────────────────────────────

export interface ImportRowError {
  field: string;
  message: string;
}

export interface ImportRow {
  rowNumber: number;
  outcome: 'VALID' | 'INVALID' | 'DUPLICATE' | 'COMMITTED' | 'SKIPPED';
  values?: Record<string, string>;
  employeeId?: string | null;
  /** Each message names its template column ("date_of_birth is required"). */
  errors?: ImportRowError[];
  /** Notes on a row that still imports — an unknown department, for one. */
  warnings?: ImportRowError[];
  employeeCode?: string | null;
  fullName?: string | null;
}

/**
 * The result of staging a file.
 *
 * Validation writes nothing to the employee table — it stages rows and reports
 * what would happen. HR sees the damage before committing it, which is the
 * whole reason the import is two steps rather than one.
 */
export interface ImportValidation {
  revision?: string;
  batchId: string;
  filename: string;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    duplicate: number;
  };
  preview: ImportRow[];
}

export interface ImportCommitResult {
  skipped?: number;
  duplicates?: number;
  failed?: number;
  replayed?: boolean;
  batchId: string;
  committed: number;
  /** Valid at review, but the code was taken by someone else before commit. */
  skippedAsRaced: number;
}

// ── Job roles and hazards ────────────────────────────────────────

export interface Hazard {
  id: string;
  code: string;
  name: string;
  description?: string | null;
}

/**
 * A job role and the hazards it exposes a worker to.
 *
 * This mapping is what the clinical protocol resolves from: the hazards decide
 * which tests are owed and how often. An unmapped role means no examination
 * can be raised for anyone holding it.
 */
export interface JobRole {
  id: string;
  code: string;
  title: string;
  description?: string | null;
  version: number;
  hazards: Hazard[];
  employeeCount: number;
}

// ── Partners and examinations ────────────────────────────────────

export interface Partner {
  id: string;
  code: string;
  name: string;
  type: string;
  /** Dr Ultimate has switched emergency provision on for it. */
  emergencyProvider?: boolean;
  /** How the business describes it. Null on venues registered before the catalogue. */
  networkType?: PartnerNetworkTypeRef;
  city?: string | null;
  state?: string | null;
  locality?: string | null;
  pincode?: string | null;
  /** Present on the list so a picker can say "empanelled" rather than imply it. */
  status?: PartnerStatus;
  dailyCapacity: number;
  testsOffered: number;
  /**
   * On this company's shortlist. Present only for a company session — the
   * platform and a venue have no preferences to answer with, and `false` for
   * them would claim every venue is un-preferred by somebody.
   */
  preferred?: boolean;
  /**
   * Straight-line kilometres from the plant asked about (`near=`), from
   * coordinates or the pincode's post offices. Null when either end has
   * neither; absent when no plant was named.
   */
  distanceKm?: number | null;
}

/**
 * Where an appointment is, as the API resolves it.
 *
 * `historical` says this is the address recorded when the slot was booked
 * rather than where the venue is today — the appointment keeps its own copy so
 * a hospital correcting its address cannot rewrite where a past examination
 * happened.
 */
export interface Venue extends Address {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  emergencyPhone: string | null;
  operatingDays: string | null;
  operatingHours: string | null;
  historical: boolean;
}

export interface PartnerAvailability {
  partnerId: string;
  partnerName: string;
  date: string;
  dailyCapacity: number;
  booked: number;
  remaining: number;
  full: boolean;
}

export interface ExaminationOrderRow {
  id: string;
  status: ExamStatus;
  examinationType: string;
  priority: string;
  dueDate: string | null;
  employeeId: string;
  plantId: string;
  assignedPartnerId: string | null;
  campaignId?: string | null;
  createdAt: string;
  /**
   * The worker this order is for, joined server-side.
   *
   * The endpoint has always returned this and the type never declared it, so
   * the examinations table resolved names by searching a separately-fetched
   * page of a hundred employees — which meant an order belonging to the
   * hundred-and-first worker displayed a truncated uuid instead of a name.
   */
  employee?: { employeeCode: string; fullName: string } | null;
  /**
   * The signed decision on a CERTIFIED case. CERTIFIED means the physician
   * signed, not that the worker was found fit — this says which. Absent for a
   * reader who sees the outcome nowhere else.
   */
  outcome?: FitnessDecision | null;
}

/** One order in full, as `GET /examinations/:id` returns it. */
export interface ExaminationDetail extends ExaminationOrderRow {
  /** When the physician signed, and — for TEMPORARILY_UNFIT — when the retest is due. */
  decidedOn?: string | null;
  retestDueOn?: string | null;
  /** The package this examination is sold under, and who chose it. */
  packageId?: string | null;
  packageSource?: 'JOB_ROLE' | 'HR' | 'CAMP' | 'DEFAULT' | null;
  package?: { id: string; code: string; name: string; version: number } | null;
  version: number;
  slaDueAt?: string | null;
  slaBreachedAt?: string | null;
  assignedPhysicianId?: string | null;
  protocolSnapshot?: unknown;
  employee?: { id?: string; employeeCode: string; fullName: string; sex?: string | null; dateOfBirth?: string | null; status?: string; version?: number } | null;
  certificate?: {
    id: string;
    certificateNumber: string;
    decision: string;
    issuedAt: string;
    validFrom: string;
    validUntil: string;
    status: string;
    generationStatus: string;
    documentRef?: string | null;
  } | null;
  appointments: Array<{
    id: string; scheduledFor: string; slotMinutes: number; venueType: string; status: string;
    checkedInAt?: string | null; cancelReason?: string | null; partnerId: string;
    venueSnapshot?: unknown; venue?: Venue | null; partner?: { name: string } | null;
  }>;
  consents?: Array<{ id: string; method?: string | null; purpose?: string | null; acceptedAt: string; withdrawnAt?: string | null }>;
  encounter?: {
    id: string;
    status: string;
    completedAt?: string | null;
    completionType?: string | null;
    partialReasonCode?: string | null;
    stations?: Array<{ id: string; station: string; status: string; sequence: number }>;
  } | null;
  events?: Array<{ id: string; occurredAt: string; fromStatus?: string | null; toStatus: string; reason?: string | null }>;
}

// ── The certifying panel ─────────────────────────────────────────

export interface PhysicianRow {
  id: string;
  fullName: string;
  registrationNo: string;
  qualification?: string | null;
  /**
   * Under the Factories Act the certifying surgeon must hold an Associate
   * Fellow of Industrial Health qualification. Without it the API refuses to
   * certify, so an unqualified name in a picker is a trap rather than a choice.
   */
  afihCertified: boolean;
  afihNumber?: string | null;
  status?: string;
  /** How to reach them. Shown to whoever may route an examination, no further. */
  email?: string | null;
  mobile?: string | null;
  city?: string | null;
  state?: string | null;
  /**
   * Whether the physician has a login at all.
   *
   * A physician with no linked account can be assigned an examination and can
   * never certify it — the order reaches review and stops. The directory has
   * always returned this; showing it in a picker is what stops somebody
   * assigning a name that cannot sign. The account id itself is never sent.
   */
  linked?: boolean;
  /** The linked login's state: INVITED until the invitation is accepted. Null when there is none. */
  loginStatus?: string | null;
  /** The older login shape, bound to one company: it cannot review any other company's cases. */
  loginCompanyBound?: boolean;
  /** Whether the physician is currently available for receiving auto-assigned reviews. */
  isAvailable?: boolean;
  /** Number of examinations currently awaiting this physician's review. */
  pendingReviews?: number;
  /** Number of examinations successfully certified by this physician. */
  completedReviews?: number;
}

// ── Hospital: queue, check-in, stations ──────────────────────────

/**
 * How reception established who is standing at the desk.
 *
 * These are not interchangeable. QR presents a token issued when the slot was
 * booked, shown once and burned on use. EMPLOYEE_CODE presents the code.
 * PHONE_SEARCH finds the appointment and proves nothing about the person, so
 * the encounter records identityVerified = false and says so on screen.
 */
export type IdentityMethod = 'QR' | 'EMPLOYEE_CODE' | 'PHONE_SEARCH';

export const IDENTITY_METHOD_LABEL: Record<IdentityMethod, string> = {
  QR: 'QR code from the employee’s app',
  EMPLOYEE_CODE: 'Employee code',
  PHONE_SEARCH: 'Phone number search',
};

export type StationStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';

export const STATION_LABEL: Record<string, string> = {
  HISTORY: 'Medical history',
  VITALS: 'Vitals',
  DENTAL: 'Dental',
  VISION: 'Vision',
  AUDIOMETRY: 'Audiometry',
  SPIROMETRY: 'Spirometry',
  RADIOLOGY: 'Radiology',
  ECG: 'ECG',
  PHLEBOTOMY: 'Phlebotomy',
  PHYSICIAN: 'Physical examination',
};

export interface QueueEmployee {
  employeeCode: string;
  fullName: string;
}

export interface AwaitingCheckIn {
  appointmentId: string;
  scheduledFor: string;
  venueType: string;
  /** Which of the venue's sites the booking is for; null when it has only one. */
  branch?: string | null;
  employee: QueueEmployee;
  examinationType: string;
  /** False means capture consent at the desk — never turn the worker away. */
  consentReady: boolean;
}

export interface InProgressEncounter {
  encounterId: string;
  checkedInAt: string;
  branch?: string | null;
  employee: QueueEmployee;
  orderStatus: ExamStatus;
  stations: Array<{ station: string; status: StationStatus }>;
  pendingStations: number;
}

export interface CompletedEncounter {
  encounterId: string;
  checkedInAt: string;
  completedAt: string;
  employee: QueueEmployee;
  orderStatus: ExamStatus;
  stations: Array<{ station: string; status: StationStatus }>;
}

export interface HospitalQueue {
  awaitingCheckIn: AwaitingCheckIn[];
  inProgress: InProgressEncounter[];
  completed?: CompletedEncounter[];
}

/** One test as it was fixed into the order's protocol snapshot. */
export interface ProtocolTest {
  id: string;
  code: string;
  name: string;
  category: string;
  unit?: string | null;
  valueType?: TestValueType;
  station?: string | null;
  isMandatory: boolean;
  /** The widest value the test can physically read; outside it is a typo. */
  plausibleRange?: { low: number | null; high: number | null } | null;
  /** The live answer set of a fixed-answer test, sent with the encounter. */
  answerOptions?: Array<{ code: string; display: string; isAbnormal?: boolean }>;
}

export interface EncounterStationRow {
  id: string;
  station: string;
  status: StationStatus;
  sequence: number;
  startedAt: string | null;
  completedAt: string | null;
  notes?: string | null;
}

/** A report file attached at a station — the machine's own paper beside the readings. */
export interface EncounterAttachmentRow {
  id: string;
  station: string;
  testId?: string | null;
  label: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface EncounterResultRow {
  id: string;
  testId: string;
  valueNumeric: number | null;
  valueText: string | null;
  valueBoolean: boolean | null;
  unit: string | null;
  flag: ResultFlag;
  attemptNo: number;
  enteredAt: string;
  validatedAt: string | null;
  releasedAt: string | null;
}

export interface EncounterDetail {
  id: string;
  checkedInAt: string;
  completedAt: string | null;
  completionType: string | null;
  identityMethod: IdentityMethod;
  stations: EncounterStationRow[];
  samples: Array<{ id: string; barcode: string; status: string; sampleType: string }>;
  attachments?: EncounterAttachmentRow[];
  results: EncounterResultRow[];
  order: {
    id: string;
    status: ExamStatus;
    examinationType: string;
    protocolSnapshot: { tests: ProtocolTest[] };
    employee: {
      employeeCode: string;
      fullName: string;
      sex: string | null;
      dateOfBirth: string | null;
    };
  };
}

// ── Laboratory ───────────────────────────────────────────────────

export type ResultFlag =
  | 'NORMAL' | 'LOW' | 'HIGH' | 'CRITICAL_LOW' | 'CRITICAL_HIGH' | 'ABNORMAL';

export const FLAG_LABEL: Record<ResultFlag, string> = {
  NORMAL: 'Normal',
  LOW: 'Low',
  HIGH: 'High',
  CRITICAL_LOW: 'Critical low',
  CRITICAL_HIGH: 'Critical high',
  ABNORMAL: 'Abnormal',
};

/**
 * The word a result's flag is shown as.
 *
 * A free-text finding is stored with the flag NORMAL because nothing reads
 * prose — "NAD" and "systolic murmur, refer" are both sentences. Printing
 * "Normal" beside either tells the reader something was judged when nothing
 * was, so a TEXT result says only that it was recorded and has to be read.
 */
export function flagWord(flag: ResultFlag, valueType?: TestValueType | null): string {
  if (valueType === 'TEXT' && flag === 'NORMAL') return 'Recorded — read the finding';
  return FLAG_LABEL[flag];
}

export type SampleStatus =
  | 'ORDERED' | 'COLLECTED' | 'IN_TRANSIT' | 'RECEIVED' | 'PROCESSING'
  | 'RESULTED' | 'VALIDATED' | 'RELEASED' | 'REJECTED' | 'RECOLLECT_REQUESTED';

/**
 * The subject as the laboratory sees them.
 *
 * Age and sex, because the reference band depends on both. Not the name: the
 * LabWorkContext projection replaces it with a pseudonym, and a bench that
 * does not know whose blood this is cannot gossip about it.
 */
export interface LabSubject {
  displayName: string;
  sex: string | null;
  dateOfBirth: string | null;
}

export interface SampleRow {
  id: string;
  barcode: string;
  sampleType: string;
  status: SampleStatus;
  collectedAt: string | null;
  receivedAt: string | null;
  encounterId: string;
  subject: LabSubject;
  /** Values on this tube the pathologist sent back and nobody has entered again. */
  sentBack?: number;
}

/** A result waiting on validation or on the second signature that releases it. */
export interface PendingResultRow {
  id: string;
  encounterId: string;
  attemptNo: number;
  testCode: string;
  testName: string;
  category: string;
  /** How the value was captured. A TEXT finding is recorded, not interpreted. */
  valueType?: TestValueType;
  value: number | string | boolean | null;
  unit: string | null;
  flag: ResultFlag;
  enteredAt: string;
  validatedAt: string | null;
  validationComment: string | null;
  stage: 'ENTERED' | 'VALIDATED';
  /** Release refuses on the entering actor's own signature. */
  enteredByYou: boolean;
  subject: LabSubject;
  /** The alert a critical value raised, if any; null once acknowledged is not implied. */
  criticalAlert?: { id: string; acknowledgedAt: string | null } | null;
  /** The band the flag was computed against. */
  referenceRange?: { low: number | null; high: number | null; criticalLow: number | null; criticalHigh: number | null } | null;
  /** This viewer validated it — release is still theirs to make if somebody else entered it. */
  validatedByYou?: boolean;
  /** Entered and validated by the same person: one pair of hands so far. */
  singleHanded?: boolean;
}

// ── Physician review ─────────────────────────────────────────────

export interface ReviewQueueRow {
  orderId: string;
  employee: QueueEmployee;
  /** The employee's company. A panel physician reviews for every company. */
  company?: string | null;
  examinationType: string;
  priority: string;
  dueDate: string | null;
  slaBreached: boolean;
  criticalFlags: number;
  /** Set for a signed review whose laboratory report was amended afterwards: a re-review. */
  reassessment?: { id: string; reportNumber: string; revision: number } | null;
  /** Set for an approved correction of the physician's own signed review, to be signed afresh. */
  correction?: { requestId: string; type: CorrectionType } | null;
  waitingSince: string;
}

export interface ClinicalResult {
  id: string;
  testCode: string;
  testName: string;
  category: string;
  value: number | string | null;
  unit: string | null;
  flag: ResultFlag;
  /** A free-text finding: recorded, never interpreted. Has to be read. */
  freeText?: boolean;
  releasedAt: string | null;
  validationComment: string | null;
  referenceRange?: { low: number | null; high: number | null; label?: string | null } | null;
  amended?: boolean;
  attemptNo?: number;
  amendedFromId?: string | null;
  priorAttempt?: {
    attemptNo: number;
    value: number | string | boolean | null;
    unit: string | null;
    flag: ResultFlag;
    releasedAt: string | null;
  } | null;
}

export interface ClinicalView {
  order: {
    id: string;
    status: ExamStatus;
    examinationType: string;
    protocolSnapshot: { tests: ProtocolTest[] };
    periodicityMonths?: number | null;
    /** How long the certificate may stand; "next review in" defaults to it. */
    certificateValidityMonths?: number | null;
    /** On a REVIEW_CORRECTION order: the examination whose signed review it corrects. */
    previousOrderId?: string | null;
  };
  /** The company the worker belongs to. */
  company?: string | null;
  employee: {
    id: string;
    employeeCode: string;
    fullName: string;
    sex: string | null;
    dateOfBirth: string | null;
    bloodGroup: string | null;
    jobRole: { code: string; title: string } | null;
    plant?: { name: string; city: string | null } | null;
  };
  hazards: Array<{ code: string; name: string }>;
  consent: { id: string; acceptedAt: string; locale: string; method: string } | null;
  encounter: {
    id: string;
    identityMethod?: IdentityMethod | string | null;
    checkedInAt: string;
    completedAt: string | null;
    completionType: string | null;
    partialReasonCode?: string | null;
    stations: EncounterStationRow[];
    samples: Array<{ barcode: string; sampleType: string; status: string }>;
    attachments?: EncounterAttachmentRow[];
  } | null;
  /** The worker's most recent certificate before this order, if any. */
  previousCertificate?: {
    id: string; certificateNumber: string; decision: FitnessDecision; status: string;
    issuedAt: string; validUntil: string; clinicalAdvice: string | null; followUpMonths: number | null;
    restrictions: Array<{ code: string; label: string }>;
  } | null;
  results: ClinicalResult[];
  abnormal: Array<{ testCode: string; value: number | string | null; flag: ResultFlag }>;
  trends: Record<string, Array<{ at: string; value: number | null; flag: ResultFlag }>>;
  existingReview: {
    id: string; signedAt: string | null; decision: FitnessDecision | null;
    /** A draft's working text, restored when the physician comes back. */
    clinicalAdvice?: string | null; remarks?: string | null;
    followUpMonths?: number | null;
    retestDueDate?: string | null;
    signatureRef?: string | null;
    /** Present once signed; what a revocation acts on. */
    certificate?: {
      id: string; status: string; certificateNumber: string;
      decision?: FitnessDecision; issuedAt?: string; validUntil?: string;
      revokedAt?: string | null; revokedReason?: string | null; supersedesId?: string | null;
    } | null;
    /** As signed, with the restriction's code and label. */
    restrictions?: Array<{ restrictionId: string; note?: string | null; restriction?: { code: string; label: string } }>;
    findings?: Array<{ id: string; category: string; finding: string; employeeVisible: boolean }>;
    physician?: { id: string; fullName: string; registrationNo: string } | null;
  } | null;
  /** Flagged if a laboratory report was amended/superseded after the physician signed the review. */
  labReportAmendedAfterSigning?: boolean;
  /** The laboratory report revision the signed decision rested on. */
  labReportBasis?: { id: string; reportNumber: string; revision: number; finalisedAt: string | null } | null;
  /** Re-reviews raised by amendments finalised after signing, open first. */
  reassessments?: ReviewReassessmentRow[];
  /** Released values with a later correction entered but not yet released. Signing is refused. */
  pendingCorrections?: string[];
  /** A laboratory report amendment that is not yet final. Signing is refused. */
  openLabReportAmendment?: { reportNumber: string; revision: number } | null;
  /** A value released after the standing report was signed. Signing is refused until it is amended. */
  staleLabReport?: { reportNumber: string } | null;
  /** Requests to correct this signed review, newest first. None of them edits it. */
  corrections?: CorrectionRequestRow[];
  /** Set once a correction was signed: the linked order that carries the corrected review. */
  correctedBy?: string | null;
  /** Set on a REVIEW_CORRECTION order: the signed review being corrected, and the request. */
  correctionOf?: {
    orderId: string;
    request: {
      id: string; type: CorrectionType; reason: string; detail: string; status: CorrectionStatus;
      requestedAt: string; reviewedAt: string | null; resolutionNote: string | null;
    };
    review: {
      id: string; decision: FitnessDecision | null; signedAt: string | null; followUpMonths: number | null;
      restrictions: Array<{ code: string; label: string }>;
      certificate: { id: string; status: string; certificateNumber: string } | null;
    } | null;
  } | null;
}

// ── Physician: correction of a signed review ─────────────────────

/** What a physician says is wrong with their own signed review. */
export type CorrectionType = 'DECISION' | 'RESTRICTION' | 'CLINICAL_ADVICE' | 'FOLLOW_UP' | 'FINDING' | 'OTHER';
export type CorrectionStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'CANCELLED';
export type CorrectionCertificateOutcome = 'UNCHANGED' | 'SUPERSEDED' | 'REVOKED' | 'ISSUED' | 'NONE';

export const CORRECTION_TYPE_LABEL: Record<CorrectionType, string> = {
  DECISION: 'Fitness decision',
  RESTRICTION: 'Restriction',
  CLINICAL_ADVICE: 'Clinical advice',
  FOLLOW_UP: 'Follow-up / re-test',
  FINDING: 'Finding',
  OTHER: 'Other',
};

export const CORRECTION_STATUS_LABEL: Record<CorrectionStatus, string> = {
  PENDING: 'Awaiting approval',
  APPROVED: 'Approved — re-review open',
  REJECTED: 'Rejected',
  COMPLETED: 'Corrected',
  CANCELLED: 'Withdrawn',
};

export const CORRECTION_OUTCOME_LABEL: Record<CorrectionCertificateOutcome, string> = {
  UNCHANGED: 'Certificate unchanged',
  SUPERSEDED: 'Certificate superseded by a new one',
  REVOKED: 'Certificate revoked',
  ISSUED: 'Certificate issued',
  NONE: 'No certificate',
};

export interface CorrectionRequestRow {
  id: string;
  type: CorrectionType;
  reason: string;
  detail: string;
  status: CorrectionStatus;
  requestedAt: string;
  reviewedAt: string | null;
  resolutionNote: string | null;
  correctionOrderId: string | null;
  completedAt: string | null;
  decisionChanged: boolean | null;
  certificateOutcome: CorrectionCertificateOutcome | null;
}

/** A correction request as the approver reads it, across companies. */
export interface CorrectionReviewRow extends CorrectionRequestRow {
  tenantId: string;
  orderId: string;
  company: string;
  physician: { fullName: string; registrationNo: string };
  review: {
    decision: FitnessDecision | null; signedAt: string | null;
    certificate: { certificateNumber: string; status: string } | null;
  };
  order: {
    examinationType: string;
    employee: { fullName: string; employeeCode: string; plant: { name: string } | null };
  };
}

// ── Physician: history and earnings ──────────────────────────────

/** Where one signature stands financially. Earned ≠ settled ≠ paid. */
export type AccrualPricing = 'PRICED' | 'UNPRICED' | 'IN_SETTLEMENT' | 'PAID';

/** One signed review in the physician's own history. */
export interface ReviewHistoryRow {
  reviewId: string;
  orderId: string;
  employee: { fullName: string; employeeCode: string };
  company: string | null;
  plant: { name: string; city: string | null } | null;
  examinationType: string;
  decision: FitnessDecision | null;
  signedAt: string;
  certificate: { certificateNumber: string; status: string } | null;
  /** An open laboratory re-review, or a correction pending or approved. */
  reReviewRequired: boolean;
  openReassessments: number;
  correction: { status: CorrectionStatus; correctionOrderId: string | null } | null;
  /** Set on a correction re-review: the order whose review it corrects. */
  correctionOfOrderId: string | null;
  earning: { status: AccrualPricing | 'NOT_PAYABLE'; amountPaise: number | null };
}

export interface AccrualLine {
  reviewId: string;
  orderId: string;
  signedAt: string;
  employee: { fullName: string; employeeCode: string };
  examinationType: string;
  company: string | null;
  plant: string | null;
  amountPaise: number | null;
  pricingStatus: AccrualPricing;
  settlementReference: string | null;
}

/** What a physician has earned in a period, before and after settlement. */
export interface PhysicianAccrual {
  period: { from: string; to: string };
  currency: string;
  signedCount: number;
  pricedCount: number;
  unpricedCount: number;
  accruedPaise: number;
  inSettlementPaise: number;
  paidPaise: number;
  outstandingPaise: number;
  lines: AccrualLine[];
}

export interface ReviewReassessmentRow {
  id: string;
  raisedAt: string;
  resolvedAt: string | null;
  outcome: 'DECISION_STANDS' | 'CERTIFICATE_REVOKED' | 'RE_REVIEWED' | null;
  note: string | null;
  labReport: { id: string; reportNumber: string; revision: number; finalisedAt: string | null; amendmentReason: string | null };
}

export interface RestrictionCodeRow {
  id: string;
  code: string;
  label: string;
  /** What a gate guard may be told. The clinical reason is never disclosed. */
  publicLabel: string;
}

// ── Public verification ──────────────────────────────────────────

/**
 * What an unauthenticated scan returns.
 *
 * REVOKED is deliberately distinct from NOT_FOUND: a guard needs to know the
 * certificate was real and has been withdrawn, which is a different situation
 * from a forged token. NOT_YET_ISSUED means the decision exists but the
 * document does not — never VALID.
 */
export type VerificationResultCode =
  | 'VALID' | 'EXPIRED' | 'REVOKED' | 'NOT_FOUND' | 'NOT_YET_ISSUED';

export interface VerificationResponse {
  result: VerificationResultCode;
  employeeName?: string;
  employeeRef?: string;
  fitnessStatus?: FitnessDecision;
  /**
   * D25, PRESENCE_ONLY: a scan learns that restrictions exist, not what they
   * are. Under QR_DISCLOSURE_MODE=FULL the public labels come through too.
   */
  hasRestrictions?: boolean;
  restrictions?: string[];
  validFrom?: string;
  validUntil?: string;
  certificateNumber?: string;
  revokedAt?: string;
  revocationReason?: string;
  verifiedAt?: string;
}

/**
 * What a tube owes, as the bench sees it.
 *
 * A technician holds lab.result-enter but not encounter.read, so this is the
 * only view of the battery available to them — pathology tests only, and a
 * pseudonym in place of the worker.
 */
export interface LabBatteryTest {
  id: string;
  code: string;
  name: string;
  unit: string | null;
  /**
   * Decides which input the bench renders. The battery is the protocol
   * snapshot's; this is the catalogue's, live — a fixed answer set configured
   * after the order was raised must reach the bench, or a technician is handed
   * a free-text box for a test that no longer has one.
   */
  valueType: TestValueType;
  isMandatory: boolean;
  loincCode?: string | null;
  /// Populated for an ENUM test. Empty for every other type.
  answerOptions: TestAnswerOption[];
  /**
   * The band THIS subject's value will be flagged against, already narrowed to
   * their age and sex. Null where they fall outside every band or the test has
   * none — the screen says so rather than implying a range exists.
   */
  referenceRange: {
    low: number | null; high: number | null;
    criticalLow: number | null; criticalHigh: number | null;
    label: string | null;
  } | null;
  /** The widest value the test can physically read; outside it is a typo. */
  plausibleRange?: { low: number | null; high: number | null } | null;
  /** The pathologist sent the last value back and it has not been re-entered. */
  sentBack?: { attemptNo: number; value: number | string | null; reason: string | null } | null;
  /**
   * The tube this test is entered against: the one asked for, or the standing
   * tube of the specimen it is drawn in. Null for a test with no specimen.
   */
  sample?: { id: string; barcode: string; sampleType: string; status: SampleStatus } | null;
  result: {
    id: string;
    attemptNo: number;
    flag: ResultFlag;
    valueNumeric: number | null;
    valueText: string | null;
    valueBoolean: boolean | null;
    valueOptionCode: string | null;
    unit: string | null;
    rangeLowAtEntry: number | null;
    rangeHighAtEntry: number | null;
    enteredAt: string;
    validatedAt: string | null;
    releasedAt: string | null;
  } | null;
}

export interface LabBattery {
  encounterId: string;
  subject: LabSubject;
  tests: LabBatteryTest[];
}

// ── Laboratory report ────────────────────────────────────────────

/**
 * The report lifecycle.
 *
 * FINAL is where it stops being editable. A correction issues revision N+1 and
 * moves the original to AMENDED, which keeps it retrievable — somebody has
 * already acted on what it said.
 */
export type LabReportStatus =
  | 'DRAFT' | 'READY_FOR_VERIFICATION' | 'VERIFIED' | 'FINAL' | 'AMENDED';

export const LAB_REPORT_STATUS_LABEL: Record<LabReportStatus, string> = {
  DRAFT: 'Draft',
  READY_FOR_VERIFICATION: 'Ready to verify',
  VERIFIED: 'Verified',
  FINAL: 'Final',
  AMENDED: 'Amended',
};

export interface LabReportLine {
  testName: string;
  testCode: string;
  loincCode: string | null;
  /// How the value was captured; decides whether it prints as a number or as prose.
  valueType?: TestValueType | null;
  value: string;
  unit: string | null;
  /// The machine unit, where configured. Metadata, never the display.
  ucumUnit?: string | null;
  referenceRange: string | null;
  /// The panic thresholds the value was judged against, where any exist.
  criticalRange?: string | null;
  flag: ResultFlag;
  /// The specimen this line came from — a heading to group under.
  group?: string | null;
  amended?: boolean;
  validationComment?: string | null;
  releasedAt?: string | null;
}

/** What the battery still owes. The backend computes it; the screen shows it. */
export interface LabReportCompleteness {
  required: number;
  completed: number;
  missing: Array<{ code: string; name: string }>;
}

/**
 * The document record for a rendered report: the columns on the row, read
 * as one object. Null until a render has succeeded.
 */
export interface LabReportDocument {
  reportId: string;
  reportType: 'LAB_REPORT';
  version: number;
  storageKey: string;
  mimeType: string;
  sizeBytes: number | null;
  sha256: string | null;
  generatedAt: string | null;
}

export interface LabReport {
  id: string;
  reportNumber: string;
  status: LabReportStatus;
  revision: number;
  encounterId: string;
  partnerId: string | null;
  supersedesId: string | null;
  generationStatus: 'PENDING' | 'ISSUED' | 'GENERATION_FAILED';
  generationError?: string | null;
  documentRef: string | null;
  documentSha256?: string | null;
  hasDocument: boolean;
  document: LabReportDocument | null;
  verifiedAt: string | null;
  finalisedAt: string | null;
  createdAt: string;
  lines: LabReportLine[];
  completeness: LabReportCompleteness;
  signatures: {
    enteredBy: string[];
    verifiedBy: string | null;
    verifiedAt: string | null;
    finalisedBy: string | null;
    finalisedAt: string | null;
    /** The role the report was signed in, from the frozen signature record. */
    signerRole?: string | null;
  };
  /** Why this revision exists (revision 2+). */
  amendmentReason?: string | null;
  /** SHA-256 of the payload, fixed at signing. */
  signedPayloadSha256?: string | null;
  /** What would stop a signature now: a correction in flight, a recollection on its way. */
  blockers?: string[];
  /**
   * Everything that stops this report moving on, typed: a stable code, the
   * sentence to show, the role that acts on it, and which acts it stops. An
   * item that stops nothing is advice (a missing signature image).
   */
  readiness?: LabReportReadiness[];
  readinessSummary?: LabReportReadinessSummary | null;
  /** Sent back with verify/finalise so a report that changed meanwhile is refused. */
  contentSha256?: string | null;
  partner?: { id: string; name: string; city: string | null } | null;
  encounter?: {
    id: string;
    checkedInAt: string | null;
    order: {
      id: string; examinationType: string; status: string;
      employee: { id: string; employeeCode: string; fullName: string };
    };
  };
}

export type LabReportAct = 'prepare' | 'verify' | 'finalise';
export interface LabReportReadiness {
  code: 'BATTERY_INCOMPLETE' | 'CORRECTION_PENDING' | 'SPECIMEN_PENDING' | 'SPECIMEN_RECORD_INCOMPLETE'
    | 'SAMPLE_LINK_MISSING' | 'NEEDS_VERIFICATION' | 'OWN_ENTRIES' | 'SIGNATURE_IMAGE_MISSING' | 'REGISTRATION_MISSING';
  category: 'RESULTS' | 'SPECIMENS' | 'CORRECTIONS' | 'REVIEW' | 'SIGNER';
  affectedCount: number;
  entity: 'test' | 'specimen' | 'result' | 'report' | 'person';
  affectedEntityIds: string[];
  /** What is incomplete. */
  message: string;
  /** Why it matters. */
  why: string;
  role: 'Laboratory technician' | 'Pathologist' | 'Another pathologist' | 'You';
  blocks: LabReportAct[];
  /** Present only when the current user may take it. */
  permittedNextAction: { action: 'OPEN_RESULT_ENTRY' | 'OPEN_RELEASE' | 'OPEN_SAMPLES' | 'OPEN_SIGNATURE' | 'VERIFY_REPORT'; label: string } | null;
}

/** Counts from typed readiness data — never from sentences. */
export interface LabReportReadinessSummary {
  mandatory: { released: number; required: number };
  pendingSpecimens: number;
  pendingCorrections: number;
  blockingIssues: number;
  signing: { canVerify: boolean; canFinalise: boolean };
}

/** GET /lab/reports?page=N — one page, the exact total, and per-status counts for the same scope and search. */
export interface LabReportPage {
  items: LabReportRow[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<'DRAFT' | 'READY_FOR_VERIFICATION' | 'VERIFIED' | 'FINAL' | 'AMENDED', number>;
}

/** One row of the laboratory's report list. Lines are fetched per report. */
export interface LabReportRow {
  id: string;
  reportNumber: string;
  status: LabReportStatus;
  revision: number;
  supersedesId: string | null;
  encounterId: string;
  partnerId: string | null;
  orderId: string;
  subject: { id: string; employeeCode: string; fullName: string };
  /** The bench's pseudonymous reference, the same on every lab screen. */
  subjectLabel?: string;
  generationStatus: 'PENDING' | 'ISSUED' | 'GENERATION_FAILED';
  generationError?: string | null;
  documentRef: string | null;
  documentSha256: string | null;
  hasDocument: boolean;
  document: LabReportDocument | null;
  verifiedAt: string | null;
  finalisedAt: string | null;
  createdAt: string;
}

/**
 * A signed report as the physician and the worker see it: the number, the
 * counts, and whether there is a document to open. FINAL and AMENDED only.
 */
export interface LabReportSummary {
  id: string;
  reportNumber: string;
  status: LabReportStatus;
  revision: number;
  supersedesId: string | null;
  encounterId: string;
  orderId: string;
  examinationType: string;
  attendedAt: string | null;
  finalisedAt: string | null;
  laboratory: { name: string; city: string | null } | null;
  lineCount: number;
  abnormalCount: number;
  criticalCount: number;
  generationStatus: 'PENDING' | 'ISSUED' | 'GENERATION_FAILED';
  hasDocument: boolean;
  document: LabReportDocument | null;
}

// ── Health Check Report ──────────────────────────────────────────

export type HealthReportAccess = 'FULL' | 'SUMMARY' | 'NONE';

export const HEALTH_REPORT_ACCESS_LABEL: Record<HealthReportAccess, string> = {
  FULL: 'Full report — HR can open the whole document',
  SUMMARY: 'Summary only — counts and the fitness decision, no values',
  NONE: 'Certificate only — no health report for HR',
};

export type HealthCardStatus = 'IN_RANGE' | 'LOW' | 'HIGH' | 'CRITICAL' | 'ABNORMAL' | 'NO_RANGE' | 'TEXT';

export const HEALTH_CARD_STATUS_LABEL: Record<HealthCardStatus, string> = {
  IN_RANGE: 'In range', LOW: 'Low', HIGH: 'High', CRITICAL: 'Critical',
  ABNORMAL: 'Abnormal', NO_RANGE: 'No range', TEXT: 'Recorded',
};

export interface HealthReportCounts {
  measured: number; withRange: number; inRange: number;
  low: number; high: number; critical: number; abnormal: number; noRange: number;
}

export interface HealthReportCard {
  code: string;
  name: string;
  technicalName?: string | null;
  loincCode?: string | null;
  panel: string;
  valueType: TestValueType;
  value: string;
  numeric: number | null;
  unit: string | null;
  low: number | null;
  high: number | null;
  criticalLow?: number | null;
  criticalHigh?: number | null;
  rangeText: string | null;
  flag: ResultFlag;
  status: HealthCardStatus;
  description: string | null;
  source: 'STATION' | 'LAB';
  previous?: { value: string; numeric: number | null; at: string; flag: ResultFlag } | null;
  comment?: string | null;
}

export interface HealthReportPanel {
  code: string;
  name: string;
  bodySystem: string;
  description: string | null;
  counts: HealthReportCounts;
  cards: HealthReportCard[];
}

export interface HealthReportSystem {
  name: string;
  panels: string[];
  counts: HealthReportCounts;
}

export interface HealthReportFitness {
  decision: FitnessDecision;
  signedAt?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  certificateNumber?: string | null;
  /// Set when the certificate named above has since been withdrawn. The
  /// number is kept so a printed report can still be matched to it.
  certificateRevoked?: boolean;
  certificateRevokedAt?: string | null;
  physician: { name: string; registrationNo?: string | null; afihNumber?: string | null } | null;
  restrictions: Array<{ code: string; label: string; note?: string | null }>;
  advice?: string | null;
  followUpMonths?: number | null;
  retestDueDate?: string | null;
  findings: Array<{ category: string; finding: string }>;
}

/** The frozen payload the PDF is rendered from — also what the screens show. */
export interface HealthReportPayload {
  reportNumber: string;
  status: 'FINAL' | 'AMENDED';
  revision: number;
  supersedesNumber?: string | null;
  issuedAt: string;
  company: { name: string } | null;
  plant: { name: string; city?: string | null } | null;
  department: { name: string } | null;
  worker: { fullName: string; employeeCode: string; sex?: string | null; dateOfBirth?: string | null; age?: number | null; jobRole?: string | null };
  examination: { id: string; type: string; attendedAt?: string | null; completedAt?: string | null };
  laboratory: { name: string; address?: string | null; phone?: string | null; email?: string | null } | null;
  samples: Array<{ barcode: string; sampleType: string; collectedAt?: string | null }>;
  labReports: Array<{ reportNumber: string; finalisedAt?: string | null }>;
  summary: HealthReportCounts & { panelsTotal: number; panelsFlagged: number; pctInRange: number | null };
  systems: HealthReportSystem[];
  panels: HealthReportPanel[];
  fitness: HealthReportFitness | null;
  previousExamination?: { at: string; type: string } | null;
}

/** A row in a list: the counts and the decision, never a value. */
export interface HealthReportSummary {
  id: string;
  reportNumber: string;
  status: 'FINAL' | 'AMENDED';
  revision: number;
  supersedesId: string | null;
  orderId: string;
  employeeId: string;
  employee: { id: string; employeeCode: string; fullName: string; plantId: string | null } | null;
  issuedAt: string;
  examinationType: string | null;
  attendedAt: string | null;
  summary: HealthReportPayload['summary'];
  systems: HealthReportSystem[];
  fitness: {
    decision: FitnessDecision; decisionLabel: string; certificateNumber: string | null;
    certificateRevoked?: boolean; certificateRevokedAt?: string | null;
    validUntil: string | null; restrictions: HealthReportFitness['restrictions'];
    followUpMonths: number | null; retestDueDate: string | null; physician: string | null;
  } | null;
  flaggedCount: number;
  generationStatus: 'PENDING' | 'ISSUED' | 'GENERATION_FAILED';
  hasDocument: boolean;
  document: (Omit<LabReportDocument, 'reportType'> & { reportType: 'HEALTH_REPORT' }) | null;
}

/** HR's list answer carries the policy so the screen can say what it may open. */
export interface HealthReportList { policy: HealthReportAccess; items: HealthReportSummary[] }

export interface HealthReportView extends Omit<HealthReportSummary, 'document'> {
  policy?: HealthReportAccess;
  /// Null under the SUMMARY policy.
  payload: HealthReportPayload | null;
  generationError?: string | null;
  document: HealthReportSummary['document'];
}

export type HealthExplanationLanguage = 'en' | 'hi';
export type HealthExplanationStatus = 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED';
export type HealthExplanationPaymentStatus = 'UNPAID' | 'PAID' | 'EXEMPT' | 'REFUNDED';

export interface HealthReportExplanationCard {
  name: string;
  value: string;
  status: string;
  plainExplanation: string;
}

export interface HealthReportExplanationPanel {
  code: string;
  name: string;
  plainMeaning: string;
  cards: HealthReportExplanationCard[];
}

export interface HealthReportExplanationContent {
  disclaimer: string;
  overview: string;
  flaggedSummary: string[];
  panels: HealthReportExplanationPanel[];
  physicianAdvicePlain: string | null;
  lifestyleTips: string[];
  questionsForDoctor: string[];
}

export interface HealthReportExplanationView {
  id?: string;
  reportId: string;
  language: HealthExplanationLanguage;
  status: HealthExplanationStatus;
  paymentStatus: HealthExplanationPaymentStatus;
  amountPaise: number;
  paidAt: string | null;
  refundedAt: string | null;
  refundReason: string | null;
  disclaimer: string | null;
  summaryText: string | null;
  content: HealthReportExplanationContent | null;
  generationError: string | null;
  generatedAt: string | null;
}

export interface Panel {
  code: string;
  name: string;
  bodySystem: string;
  description: string | null;
  sortOrder: number;
}

/** What a download route returns: a grant that dies in minutes, never the bytes. */
export interface DocumentGrant {
  url: string;
  expiresAt: string;
  reportNumber: string;
  documentSha256: string | null;
  sizeBytes: number | null;
  mimeType: string;
}

// ── Onboarding: companies ────────────────────────────────────────

export type TenantStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';

export const TENANT_STATUS_LABEL: Record<TenantStatus, string> = {
  PENDING: 'Awaiting review',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  CLOSED: 'Closed',
};

/**
 * Where a registration is in its paperwork.
 *
 * A checklist rather than a state machine: documents arrive in any order, and
 * the only transition that carries consequences — PENDING to ACTIVE — is the
 * approval, not this field.
 */
export const ONBOARDING_STATES = [
  'DOCS_PENDING', 'DOCS_RECEIVED', 'AGREEMENT_SIGNED', 'READY_FOR_REVIEW',
] as const;

export const ONBOARDING_LABEL: Record<string, string> = {
  DOCS_PENDING: 'Documents pending',
  DOCS_RECEIVED: 'Documents received',
  AGREEMENT_SIGNED: 'Agreement signed',
  READY_FOR_REVIEW: 'Ready for review',
  ACTIVE: 'Onboarded',
};

export const WORKFORCE_BANDS = ['1-50', '51-100', '101-500', '501-1000', '1000+'] as const;
export type WorkforceBand = (typeof WORKFORCE_BANDS)[number];

export interface CompanyRow {
  id: string;
  name: string;
  legalName: string | null;
  gstin: string | null;
  industry: string | null;
  industryId?: string | null;
  industryRef?: { id: string; code: string; name: string } | null;
  city?: string | null;
  state?: string | null;
  status: TenantStatus;
  onboardingState: string;
  setupMode?: SetupMode;
  setupStatus?: SetupStatus;
  workforceBand?: WorkforceBand | null;
  workforceCount?: number | null;
  employees?: number | null;
  createdAt: string;
  /** null when the reader may not see inside this company — not zero. */
  plants: number | null;
  users: number | null;
}

/**
 * The company profile extras a platform administrator keeps (legacy-app parity
 * P2): statutory identity beside the GSTIN, the group company, the camp
 * coordinator and the site's emergency numbers. Read-only for HR.
 */
export interface CompanyExtras {
  pan?: string | null;
  parentTenantId?: string | null;
  parentTenant?: { id: string; name: string } | null;
  coordinatorName?: string | null;
  coordinatorEmail?: string | null;
  coordinatorDesignation?: string | null;
  coordinatorMobile?: string | null;
  coordinatorAltMobile?: string | null;
  emergencyAmbulance?: string | null;
  emergencyFire?: string | null;
  emergencyPolicePhone?: string | null;
  emergencyPoliceAddress?: string | null;
}

/** A signed agreement on a company's file. The bytes come through a five-minute grant. */
export interface CompanyAgreementRow {
  id: string;
  title: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  signedOn: string | null;
  validUntil: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

export interface CompanyDetail extends Address, ContactDetails, CompanyExtras {
  id: string;
  name: string;
  legalName: string | null;
  gstin: string | null;
  industry: string | null;
  industryId?: string | null;
  industryRef?: { id: string; code: string; name: string } | null;
  contactDesignation?: string | null;
  website?: string | null;
  status: TenantStatus;
  onboardingState: string;
  createdAt: string;
  updatedAt?: string;
  workforceBand?: WorkforceBand | null;
  workforceCount?: number | null;
  employeeCodePrefix?: string | null;
  employeeCodeNextSeq?: number;
  setupMode?: SetupMode;
  setupStatus?: SetupStatus;
  setupSteps?: Record<string, { doneAt?: string; skippedAt?: string }> | null;
  setupCompletedAt?: string | null;
  welcomeEmailSentAt?: string | null;
  /**
   * When the server itself looked the GSTIN up at GST and the company was
   * registered from that lookup. Null for a GSTIN typed by hand, and cleared
   * when the GSTIN changes. `gstStatus` is GST's word ("Active"), not `status`.
   */
  gstVerifiedAt?: string | null;
  gstStatus?: string | null;
  gstLegalName?: string | null;
  /**
   * Whether this reader could see the company's org chart at all.
   *
   * False for a platform session: the registry policy opens the company row
   * and nothing under it, so plants and users come back empty — and an empty
   * list rendered as "0 plants" states something about the customer that is
   * really a fact about the reader. Reaching inside is the Oversight path,
   * with its own permission and its own written justification.
   */
  orgVisible?: boolean;
  plants: Array<Plant & { departments?: number; employees?: number }>;
  users: Array<{
    id: string; email: string; fullName: string; status: string;
    lastLoginAt: string | null; roles: string[];
  }>;
}

// ── Onboarding: users and roles ──────────────────────────────────

export type UserStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED';

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  INVITED: 'Invited — not yet signed in',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  DEACTIVATED: 'Deactivated',
};

/** A role the current caller is actually able to grant. */
export interface GrantableRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  scopeType: 'GLOBAL' | 'TENANT' | 'PLANT' | 'PARTNER' | 'CAMP' | 'SELF';
  permissionCount: number;
  /** Offered by default. False for roles kept for compatibility (docs/37 §4). */
  listed: boolean;
}

export interface ConsoleUserRow {
  id: string;
  email: string;
  fullName: string;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
  suspendedReason: string | null;
  /**
   * `scopeType` is the assignment's; `roleScope` is the role's own defined
   * reach (TENANT, PLANT, CAMP for a company's roles; PARTNER, SELF, GLOBAL for
   * a venue's, the panel's or the platform's).
   */
  roles: Array<{ key: string; name: string; scopeType: string; scopeIds: string[]; roleScope?: string }>;
}

// ── Onboarding: the partner network ──────────────────────────────

export type PartnerStatus =
  | 'ONBOARDING' | 'ACTIVE' | 'REJECTED' | 'SUSPENDED' | 'TERMINATED';

/**
 * REJECTED and TERMINATED read differently on purpose: declined means the
 * application was reviewed and refused, terminated means the relationship was
 * ours and ended. A report that cannot tell those apart is not much of a report.
 */
export const PARTNER_STATUS_LABEL: Record<PartnerStatus, string> = {
  ONBOARDING: 'Awaiting review — not routable',
  ACTIVE: 'Empanelled',
  REJECTED: 'Declined',
  SUSPENDED: 'Suspended',
  TERMINATED: 'Terminated',
};

/**
 * What the platform DOES with a venue. Routing, capacity and the appointment
 * card read this, so it stays a fixed set rather than configurable data.
 */
export type PartnerBehaviour = 'HOSPITAL' | 'LABORATORY' | 'BOTH';

/**
 * How the business DESCRIBES a venue — configurable master data.
 *
 * Separate from `PartnerBehaviour` on purpose: a collection centre, an imaging
 * centre and a pathology lab are all LABORATORY to the routing code and are
 * not the same commercial relationship. `behaviour` is what choosing this type
 * writes to `Partner.type`, so one choice on the form stores both values.
 */
export interface PartnerNetworkType {
  id: string;
  code: string;
  name: string;
  behaviour: PartnerBehaviour;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  /** How many venues carry this type. A type in use cannot be deleted. */
  partnersUsing?: number;
}

/** The type as it appears on a partner: null for venues registered before the catalogue. */
export type PartnerNetworkTypeRef =
  { id: string; code: string; name: string; behaviour?: PartnerBehaviour } | null;

/**
 * One test on a venue's catalogue: the test's identity plus the venue's own
 * terms for it. No price — that is the live payee rate card's business.
 */
export interface PartnerCatalogueRow {
  id: string;
  code: string;
  name: string;
  category: string;
  unit: string | null;
  sampleType: SampleType;
  /** The lab's own code, as its report and invoice will print it. */
  partnerCode: string | null;
  /** Promised turnaround from receipt, in minutes. Null is unpromised. */
  tatMinutes: number | null;
  nablScope: boolean;
  homeCollection: boolean;
  /** ISO date. While it is today or later, routing treats the test as absent here. */
  pausedUntil: string | null;
  pausedReason: string | null;
  /** Computed server-side from `pausedUntil`, so no screen re-derives it. */
  paused: boolean;
  notes: string | null;
  updatedAt: string;
  /** What the venue is paid per result today, from the payee rate card in force. Null: no card prices it. */
  rateTodayPaise?: number | null;
}

// ── What a payee is paid: versioned rate cards ───────────────────

export type BillableEventKind = 'ENCOUNTER_COMPLETED' | 'TEST_RESULTED' | 'REPORT_REVIEW';

export const BILLABLE_EVENT_LABEL: Record<BillableEventKind, string> = {
  ENCOUNTER_COMPLETED: 'Per employee attended',
  TEST_RESULTED: 'Per test resulted',
  REPORT_REVIEW: 'Per review signed',
};

export interface PayeeRateLineRow {
  id: string;
  event: BillableEventKind;
  testId: string | null;
  test?: { id: string; code: string; name: string } | null;
  /** Set when the rate is one agreed price for a billing profile's tests. */
  profileId?: string | null;
  profile?: { id: string; code: string; name: string; version: number; _count?: { tests: number } } | null;
  /** On a test rate: charged even when a billed profile covers the test. */
  chargeInsideProfile?: boolean;
  label: string;
  amountPaise: number;
}

/**
 * A billing profile: a named set of tests priced as one item. Separate from
 * report grouping — where a test prints never decides what it costs.
 */
export interface BillingProfileRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  version: number;
  tests: Array<{ id: string; code: string; name: string; category?: string }>;
  /** How many partner / customer rate lines price it. */
  partnerRates: number;
  customerPrices: number;
  /** True once a published card prices it: its tests can no longer change. */
  contentsLocked: boolean;
}

/** Payable to the lab, charged to customers, and the gap, for a statement. */
export interface LabStatementSummary {
  payablePaise: number;
  sellingPaise: number;
  /** Selling minus payable over the lines that have both — before other costs and taxes. */
  grossMarginPaise: number;
  linesWithoutSelling: number;
  payableWithoutSellingPaise: number;
}

export interface PayeeRateCardRow {
  id: string;
  payeeType: 'PARTNER' | 'PHYSICIAN';
  partnerId: string | null;
  physicianId: string | null;
  name: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: RateCardStatus;
  publishedAt: string | null;
  notes: string | null;
  lines: PayeeRateLineRow[];
}

// ── What the platform owes: frozen statements ────────────────────

export type SettlementStatus = 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOID';

export const SETTLEMENT_STATUS_LABEL: Record<SettlementStatus, string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  PARTIALLY_PAID: 'Part paid',
  PAID: 'Paid',
  VOID: 'Void',
};

export interface SettlementLineRow {
  id: string;
  event: BillableEventKind;
  /** Which company's work this line covers. Attribution, not scope. */
  tenantId: string;
  testId: string | null;
  test?: { id: string; code: string; name: string } | null;
  label: string;
  quantity: number;
  /** The rate that applied, frozen — never read back through the card. */
  ratePaise: number;
  amountPaise: number;
  profileId?: string | null;
  profileVersion?: number | null;
  profile?: { id: string; code: string; name: string; tests: Array<{ test: { id: string; code: string; name: string } }> } | null;
  companyName?: string | null;
  /** Platform view only: what the customer is charged for the same item. */
  sellingUnitPaise?: number | null;
  sellingAmountPaise?: number | null;
  sellingBasis?: string | null;
}

export interface SettlementPaymentRow {
  id: string;
  amountPaise: number;
  paidOn: string;
  method: string;
  reference: string | null;
  notes: string | null;
  /** Set on the row that undoes an earlier payment, pointing at what it reverses. */
  reversesId: string | null;
  reversalReason: string | null;
  createdAt: string;
}

/**
 * One payee's frozen period.
 *
 * `paidPaise` and `outstandingPaise` are computed by the API from the payments
 * rather than stored, for the same reason the lines are derived: a stored
 * balance drifts from the payments it claims to summarise.
 */
export interface SettlementRow {
  id: string;
  payeeType: 'PARTNER' | 'PHYSICIAN';
  partnerId: string | null;
  physicianId: string | null;
  reference: string;
  periodStart: string;
  periodEnd: string;
  status: SettlementStatus;
  currency: string;
  subtotalPaise: number;
  sourceCount: number;
  /** Events no published card priced. Non-zero means the statement is short. */
  unpricedCount: number;
  issuedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  notes: string | null;
  paidPaise: number;
  outstandingPaise: number;
  partner?: { id: string; code: string; name: string } | null;
  physician?: { id: string; fullName: string; registrationNo?: string } | null;
  lines?: SettlementLineRow[];
  payments?: SettlementPaymentRow[];
  /** Platform view of a lab statement only. */
  summary?: LabStatementSummary | null;
}

/**
 * What a period would settle at, before anything is frozen.
 *
 * Read-only: asking does not create a statement. `unpricedCount` is the
 * events no published card priced — never charged at zero, never dropped.
 */
export interface SettlementPreview {
  period: { from: string; to: string };
  lines: Array<{
    event: BillableEventKind;
    tenantId: string;
    testId: string | null;
    profileId?: string | null;
    label: string;
    quantity: number;
    ratePaise: number;
    amountPaise: number;
    sellingUnitPaise?: number | null;
    sellingAmountPaise?: number | null;
    sellingBasis?: string | null;
  }>;
  summary?: LabStatementSummary;
  subtotalPaise: number;
  sourceCount: number;
  unpricedCount: number;
}

/** Charged minus paid, per period. Derived from frozen invoice and settlement lines. */
export interface MarginReport {
  period: { from: string; to: string };
  totals: { orders: number; billedPaise: number; payoutPaise: number; marginPaise: number; unsettledEvents: number };
  byCompany: Array<{ tenantId: string; name: string; orders: number; billedPaise: number; payoutPaise: number; marginPaise: number; unsettledEvents: number }>;
  byPayee: Array<{ payeeType: 'PARTNER' | 'PHYSICIAN'; payeeId: string; name: string; orders: number; events: number; payoutPaise: number }>;
  orders: Array<{ id: string; tenantId: string; company: string | null; employeeCode: string; examinationType: string; invoiceNumber: string; invoiceStatus: string; billedPaise: number; payoutPaise: number; marginPaise: number; unsettled: number }>;
}

export interface PartnerDetail extends Address, ContactDetails {
  id: string;
  code: string;
  name: string;
  emergencyProvider?: boolean;
  type: string;
  /** How the business describes it. Null on venues registered before the catalogue. */
  networkType?: PartnerNetworkTypeRef;
  /**
   * Who decided, and when.
   *
   * A convenience for the screen, never the evidence — the audit trail is that.
   * All null on venues empanelled before these columns existed, and the name is
   * null when the operator's account has since been removed; the decision
   * outlives the account, so the date still stands on its own.
   */
  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  /** Answered outside desk hours — a worker at a closed gate rings this. */
  emergencyPhone: string | null;
  website: string | null;
  operatingDays: string | null;
  operatingHours: string | null;
  dailyCapacity: number;
  status: PartnerStatus;
  createdAt: string;
  /** What this venue can run, and on what terms. Routing checks the protocol against this list. */
  capabilities: PartnerCatalogueRow[];
  /** The branches, primary first. */
  locations?: PartnerLocation[];
}

// ── The org chart below the plant ────────────────────────────────

export interface DepartmentRow {
  id: string;
  code: string;
  name: string;
  plantId: string;
  plantCode: string;
  plantName: string;
  employeeCount: number;
}

export interface DesignationRow {
  id: string;
  code: string;
  title: string;
  employeeCount: number;
}

export interface ContractorRow {
  id: string;
  code: string;
  name: string;
  licenceNo: string | null;
  licenceExpiry: string | null;
  contactName: string | null;
  contactPhone: string | null;
  status: string;
  employeeCount: number;
  /** A finding at an inspection in its own right, separately from screening. */
  licenceExpired: boolean;
}

// ── The audit trail ──────────────────────────────────────────────

export interface AuditRow {
  id: string;
  tenantId: string | null;
  actorId: string | null;
  actorRole: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  outcome: string;
  ip: string | null;
  occurredAt: string;
  before: unknown;
  after: unknown;
  metadata: unknown;
}

// ── Campaigns ────────────────────────────────────────────────────

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Launched — orders raised',
  ACTIVE: 'Active — cohort notified',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export interface CampaignRow {
  id: string;
  name: string;
  examinationType: string;
  status: CampaignStatus;
  startsOn: string;
  endsOn: string;
  plantIds: string[];
  departmentIds: string[];
  jobRoleIds: string[];
  /**
   * Set at launch and never re-evaluated. A campaign that keeps re-resolving
   * its cohort has a moving compliance denominator, and then nobody can answer
   * "were we compliant in March?".
   */
  cohortFrozenAt: string | null;
  cohortSize: number | null;
  assignedPartnerId: string | null;
  launchedAt: string | null;
  completedAt: string | null;
  cancelReason: string | null;
}

export interface CampaignProgress {
  total: number;
  certified: number;
  openOrderCount: number;
  openNoShowCount: number;
  anyCheckedIn: boolean;
}

export type CampaignDetail = CampaignRow & { progress: CampaignProgress };

/** The cohort a campaign would cover, counted before it exists. */
export interface CohortPreview {
  campaignId: string;
  cohortSize: number;
  /** Of those, workers who already have a case open and will be passed over at launch. */
  alreadyOpen?: number;
  frozen: boolean;
  sample: Array<{ employeeCode: string; fullName: string }>;
}

export interface LaunchResult {
  campaignId: string;
  status: string;
  generated: number;
  /**
   * Every worker the campaign could not cover. Surfaced rather than buried:
   * each one still owes a statutory examination.
   */
  skipped: Array<{ employeeCode: string; reason: string }>;
}

// ── The clinical catalogue ───────────────────────────────────────

/**
 * Mirrors the Prisma TestCategory enum exactly.
 *
 * 'OTHER' used to be listed here and has never been a member of that enum, so
 * a test created with it passed every check and failed at the database. A
 * category is what routes a test to a station; a catch-all routes it nowhere.
 */
export type TestCategory =
  | 'VITALS' | 'VISION' | 'AUDIOMETRY' | 'SPIROMETRY' | 'RADIOLOGY'
  | 'ECG' | 'PATHOLOGY' | 'PHYSICAL_EXAM';

export const TEST_CATEGORY_LABEL: Record<TestCategory, string> = {
  VITALS: 'Vitals',
  VISION: 'Vision',
  AUDIOMETRY: 'Audiometry',
  SPIROMETRY: 'Spirometry',
  RADIOLOGY: 'Radiology',
  ECG: 'ECG',
  PATHOLOGY: 'Pathology',
  PHYSICAL_EXAM: 'Physical examination',
};

/**
 * One reference band.
 *
 * A null `sex` or age bound means "no constraint on that dimension". A more
 * specific band always wins over a general one when a value is flagged, which
 * is why one range per test is clinically wrong: 11.8 g/dL haemoglobin is mild
 * anaemia in an adult male and normal in an adult female.
 */
export interface ReferenceRangeRow {
  id?: string;
  sex: 'MALE' | 'FEMALE' | 'OTHER' | null;
  ageMinYears: number | null;
  ageMaxYears: number | null;
  low: number | null;
  high: number | null;
  criticalLow: number | null;
  criticalHigh: number | null;
  label: string | null;
}

/**
 * How a value is captured. The Lab Console renders its input from this and
 * nothing else — a bench screen that hardcodes a field per test is one new
 * test away from being wrong.
 */
export type TestValueType = 'NUMERIC' | 'TEXT' | 'BOOLEAN' | 'ENUM';

/** One allowed answer for an ENUM test. */
export interface TestAnswerOption {
  id?: string;
  /// Stable and ours. A result stores this; `display` may be reworded freely.
  code: string;
  display: string;
  sequence: number;
  /// Whether choosing this answer flags the result ABNORMAL. A clinical fact
  /// about the option, not a rendering decision.
  isAbnormal: boolean;
  isActive?: boolean;
  loincAnswerCode?: string | null;
}

/**
 * A test's universal identity, where one exists.
 *
 * Always optional. A Standard Threshold Shift is an OSHA calculation rather
 * than a LOINC observation, and a wrong code is indistinguishable from a right
 * one downstream — so an unmapped test stays unmapped.
 */
export interface LoincMapping {
  loincCode: string | null;
  loincLongName: string | null;
  /// The machine unit. Distinct from `unit`, which is what a report prints:
  /// we show "mmHg", UCUM says "mm[Hg]".
  ucumUnit: string | null;
  /**
   * What a WORKER reads on their own record.
   *
   * Three names, three audiences. `loincLongName` is for an analyzer and a
   * physician; `name` is what staff call it; this is what the employee app
   * prints. Null falls back to `name`, which is always safe to show.
   */
  consumerName: string | null;
}

export interface CatalogueTest extends LoincMapping {
  id: string;
  code: string;
  name: string;
  category: TestCategory;
  unit: string | null;
  valueType: TestValueType;
  station: string | null;
  isActive: boolean;
  /// The body-system panel on the worker's Health Check Report; null falls back to the category.
  panelCode?: string | null;
  /// What this measures, in a worker's words.
  description?: string | null;
  /// The tube it is drawn into; NONE for a station measurement.
  sampleType?: SampleType;
  /// Hours of fasting beforehand; null when none is needed.
  fastingHours?: number | null;
  sexApplicable?: 'MALE' | 'FEMALE' | 'OTHER' | null;
  ageMinYears?: number | null;
  ageMaxYears?: number | null;
  /// Worker-facing preparation: "Bring your spectacles".
  preparationNotes?: string | null;
  /// The widest value a numeric test can physically read; entry outside is refused.
  plausibleLow?: number | null;
  plausibleHigh?: number | null;
  referenceRanges: ReferenceRangeRow[];
  answerOptions?: TestAnswerOption[];
  answerOptionCount?: number;
  usedByProtocols: number;
  /** Which default packages carry it: PRE_EMPLOYMENT, YEARLY, both or neither. */
  inDefaultPackages?: Array<'PRE_EMPLOYMENT' | 'YEARLY'>;
  resultCount: number;
}

/** One LOINC term, as the Test Master picker shows it. */
export interface LoincHit {
  loincCode: string;
  longCommonName: string;
  shortName: string | null;
  /// The patient-friendly name. Used in the Employee App, never in a picker
  /// aimed at an administrator.
  consumerName: string | null;
  ucumUnit: string | null;
  class: string | null;
  system: string | null;
  scaleType: string | null;
  /// Lower is more commonly ordered. 0 means LOINC published no rank.
  commonRank: number;
  status: string;
  isUniversalOrder: boolean;
  mapsTo: string | null;
  licenceNotice: string | null;
}

export interface LoincSearchResult {
  items: LoincHit[];
  query: string;
  attribution?: string;
}

export interface LoincStatus {
  imported: boolean;
  terms: number;
  universalOrderTerms: number;
  loincVersion: string | null;
  attribution: string;
  hint: string | null;
}

export interface CatalogueHazard {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  protocolCount: number;
  jobRoleCount: number;
}

export interface CatalogueProtocol {
  /**
   * The LOINC panel this battery corresponds to, where one does.
   *
   * A label for the outside world — a partner order that says "CBC panel
   * 58410-2" rather than eight component codes — and never a substitute for
   * the test list. Usually null: an occupational battery is hazard-driven and
   * rarely equals a published panel exactly.
   */
  loincPanelCode?: string | null;
  id: string;
  code: string;
  /**
   * Batteries are versioned, never edited. An examination snapshots its
   * protocol at creation, so changing a published version would make "were we
   * compliant in March?" unanswerable.
   */
  version: number;
  name: string;
  description: string | null;
  periodicityMonths: number;
  certificateValidityMonths: number;
  requiresConsent: boolean;
  isActive: boolean;
  effectiveFrom: string;
  hazards: Array<{ id: string; code: string; name: string }>;
  tests: Array<{
    id: string; code: string; name: string; category: string;
    station: string | null; isMandatory: boolean; sequence: number;
  }>;
}

export interface CataloguePackage {
  /**
   * The LOINC panel this battery corresponds to, where one does.
   *
   * A label for the outside world — a partner order that says "CBC panel
   * 58410-2" rather than eight component codes — and never a substitute for
   * the test list. Usually null: an occupational battery is hazard-driven and
   * rarely equals a published panel exactly.
   */
  loincPanelCode?: string | null;
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  /**
   * PRE_EMPLOYMENT or YEARLY on the two defaults every company runs (owner's
   * decision, 29 Oct 2026); null otherwise.
   */
  defaultFor?: 'PRE_EMPLOYMENT' | 'YEARLY' | null;
  tests: Array<{ id: string; code: string; name: string; category: string }>;
}

/** One test, and everything that points at it. */
export interface CatalogueTestDetail extends Omit<CatalogueTest, 'usedByProtocols' | 'resultCount'> {
  createdAt: string;
  updatedAt: string;
  answerOptions: TestAnswerOption[];
  /// Retired options, kept so a past result stays explainable.
  retiredAnswerOptions: TestAnswerOption[];
  supersededRanges: Array<ReferenceRangeRow & { supersededAt: string | null; createdAt: string }>;
  protocols: Array<{ id: string; code: string; version: number; name: string; isActive: boolean; isMandatory: boolean }>;
  packages: Array<{ id: string; code: string; version: number; name: string; isActive: boolean; defaultFor?: string | null }>;
  resultCount: number;
  partnerCount: number;
  configurationOverrides: number;
}

export interface CatalogueHazardDetail extends Omit<CatalogueHazard, 'protocolCount'> {
  createdAt: string;
  updatedAt: string;
  protocols: Array<{ id: string; code: string; version: number; name: string; isActive: boolean; periodicityMonths: number; testCount: number }>;
  industries: Array<{ id: string; code: string; name: string; isActive: boolean }>;
  rules: Array<{ id: string; name: string; tier: InclusionLevel; package: { id: string; code: string; name: string } }>;
}

export interface CatalogueProtocolDetail extends CatalogueProtocol {
  createdAt: string;
  tests: Array<CatalogueProtocol['tests'][number] & { unit: string | null; valueType: string }>;
  versions: Array<{ id: string; version: number; name: string; isActive: boolean; effectiveFrom: string; testCount: number }>;
  rateCardLines: number;
}

export interface CataloguePackageDetail extends CataloguePackage {
  version: number;
  category: string | null;
  effectiveFrom: string;
  createdAt: string;
  tests: Array<{ id: string; code: string; name: string; category: string; station: string | null; isActive: boolean }>;
  industries: Array<{ id: string; code: string; name: string; isActive: boolean; tier: InclusionLevel }>;
  rules: Array<{ id: string; name: string; tier: InclusionLevel; priority: number; examinationType: string | null; industry: { id: string; name: string } | null; hazard: { id: string; name: string } | null }>;
  versions: Array<{ id: string; version: number; name: string; isActive: boolean; effectiveFrom: string }>;
  configurationCount: number;
}

/** One appointment in full, as `GET /appointments/:id` returns it. */
export interface AppointmentDetail extends Omit<AppointmentRow, 'partner' | 'order'> {
  rescheduledFromId: string | null;
  partnerLocationId: string | null;
  campaignVenueId: string | null;
  checkInTokenPrefix: string | null;
  createdAt: string;
  updatedAt: string;
  partner: { id: string; code: string; name: string; type: string };
  venue: Venue | null;
  order: {
    id: string; examinationType: string; status: string; plantId: string; campaignId: string | null;
    priority: string; dueDate: string | null; assignedPhysicianId: string | null;
    employee: { id: string; employeeCode: string; fullName: string; mobile: string | null };
  };
  encounter: {
    id: string; checkedInAt: string; completedAt: string | null; completionType: string | null;
    stations: Array<{ id: string; station: string; status: string; sequence: number }>;
  } | null;
  replaced: { id: string; scheduledFor: string; status: string; cancelReason: string | null } | null;
  replacedBy: { id: string; scheduledFor: string; status: string } | null;
}

/** One certificate in full, as `GET /certificates/:id` returns it. */
export interface CertificateDetail {
  id: string;
  certificateNumber: string;
  decision: string;
  issuedAt: string;
  validFrom: string;
  validUntil: string;
  status: string;
  generationStatus: string;
  generationError?: string | null;
  documentRef?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  supersedesId?: string | null;
  templateCode?: string;
  templateVersion?: number;
  employee: { id: string; employeeCode: string; fullName: string; plantId: string };
  physician: { id: string; fullName: string; registrationNo: string; afihNumber: string | null };
  order: { id: string; examinationType: string; status: string; campaignId: string | null };
  /** Employer-safe: the physician's advice and remarks are never on a certificate. */
  review: {
    id: string; decision: string | null; followUpMonths?: number | null;
    retestDueDate?: string | null; signedAt?: string | null;
    restrictions: Array<{ restriction: { id: string; code: string; label: string; publicLabel: string } }>;
  };
  tokens: Array<{ tokenPrefix: string; expiresAt: string }>;
}

export interface CatalogueRestriction {
  id: string;
  code: string;
  label: string;
  publicLabel: string;
  description: string | null;
  isActive: boolean;
  timesApplied: number;
}

// ── Platform oversight ──────────────────────────────────────────────
// The shape of a view from above. Counts, never rows: a field here that
// named a company, a worker or a certificate would be a disclosure the
// permission behind it does not buy.

export interface PlatformOverview {
  generatedAt: string;
  /** How many companies contributed. PENDING registrations hold nothing. */
  companiesCounted: number;
  tenants: Record<string, number>;
  /** The workforce: active workers on the rolls. Not a measure of coverage. */
  employees: number;
  /** Active workers holding a current certificate — ACTIVE, issued, validity not passed. */
  employeesWithCurrentCertificate?: number;
  examinations: Record<string, number>;
  campaigns: Record<string, number>;
  certificates: Record<string, number> & {
    expiringWithin90Days: number;
    /** Validity has passed while the row still reads ACTIVE — a renewal that did not run. */
    pastValidityStillActive: number;
  };
  network: { partners: number; physicians: number };
  attention: { unacknowledgedCriticalResults: number; examinationsPastSla: number };
  /** This month so far, and the same number of days at the start of last month. */
  thisMonth?: PlatformMonth;
  lastMonthSameDays?: PlatformMonth;
}

export interface PlatformMonth {
  from: string;
  to: string;
  companiesRegistered: number;
  employeesAdded: number;
  certificatesIssued: number;
  casesRaised: number;
}

/**
 * The platform pipeline. Distinct from the HR `Pipeline`: this one is summed
 * across every company and carries the breach count per stage, which a single
 * company's funnel has no use for.
 */
export interface PlatformPipelineStage {
  key: string;
  label: string;
  count: number;
  /** Of those, how many are already past the time the company was promised. */
  breached: number;
}

export interface PlatformPipeline {
  generatedAt: string;
  stages: PlatformPipelineStage[];
  inFlight: number;
  breached: number;
}

/** One thing that needs a human today, as the platform dashboard renders it. */
export interface PlatformAlert {
  key: string;
  label: string;
  count: number;
  severity: 'critical' | 'warning' | 'info';
  detail: string;
  /**
   * Where the API believes this should lead. Advisory only — some targets are
   * screens that do not exist yet, so the client decides whether to link.
   */
  href: string;
}

export interface PlatformAlerts {
  generatedAt: string;
  alerts: PlatformAlert[];
}

export interface PlatformActivityRow {
  id: string;
  action: string;
  /** The action in words, resolved server-side so four clients do not each map it. */
  label: string;
  entityType: string;
  entityId: string | null;
  actorRole: string | null;
  outcome: string;
  occurredAt: string;
}

export interface PlatformActivity {
  generatedAt: string;
  items: PlatformActivityRow[];
}

export interface OversightPlant {
  id: string; name: string; code: string;
  city: string | null; state: string | null; status: string;
  departments: number; employees: number;
}

/** Establishment detail. Nothing clinical crosses a company boundary. */
export interface OversightEmployee {
  id: string; employeeCode: string; fullName: string; status: string;
  plant: { name: string } | null;
  department: { name: string } | null;
  jobRole: { title: string } | null;
}

export interface OversightExamination {
  id: string; status: string; examinationType: string; priority: string;
  dueDate: string | null; slaDueAt: string | null; slaBreachedAt: string | null;
  createdAt: string;
  employee: { employeeCode: string; fullName: string };
}

/** The registry, deliberately without the fitness decision. */
export interface OversightCertificate {
  id: string; certificateNumber: string; status: string;
  issuedAt: string; validFrom: string; validUntil: string;
  generationStatus: string;
  revokedAt: string | null; revokedReason: string | null;
  employee: { employeeCode: string };
}

/** Who is wearing whose identity, and why. Read-only sessions, capped at 15 minutes. */
export interface ImpersonationRow {
  id: string;
  actor: { email: string; fullName: string };
  target: { email: string; fullName: string };
  targetRoles: string[];
  reason: string;
  ticketRef: string | null;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  requestCount: number;
  strippedPermissions: string[];
  active: boolean;
}

// ── Billing ─────────────────────────────────────────────────────────
// Every amount is an integer count of PAISE. A rupee amount as a float
// would have to be multiplied by 100 somewhere, and 14.99 * 100 is
// 1498.9999999999998 — the client formats, the wire carries integers.

export type RateCardStatus = 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'VOID';
export type TaxTreatment = 'UNDETERMINED' | 'GST_STANDARD' | 'EXEMPT' | 'REVERSE_CHARGE';

export const TAX_TREATMENT_LABEL: Record<TaxTreatment, string> = {
  UNDETERMINED: 'Not decided yet',
  GST_STANDARD: 'Standard GST',
  EXEMPT: 'Healthcare exemption',
  REVERSE_CHARGE: 'Reverse charge',
};

export interface RateCardLineRow {
  id: string;
  label: string;
  amountPaise: number;
  protocolId: string | null;
  examinationType: string | null;
  /** The price of an examination sold under this package. */
  packageId?: string | null;
  /** The ADDITIVE price of this test when prescribed outside the protocol. */
  testId?: string | null;
  /** The customer selling price of a billing profile. */
  profileId?: string | null;
  protocol?: { name: string; code: string } | null;
  package?: { name: string; code: string } | null;
  test?: { name: string; code: string } | null;
  profile?: { name: string; code: string } | null;
}

export interface RateCardRow {
  id: string;
  name: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: RateCardStatus;
  publishedAt: string | null;
  notes: string | null;
  lines: RateCardLineRow[];
}

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft — not sent',
  ISSUED: 'Issued',
  VOID: 'Void',
};

/** The plant an invoice bills (docs/37 A6). */
export interface InvoicePlant { id: string; code: string; name: string }

/** Where a frozen bill-to came from: the plant's own GSTIN, plant details with the company's GSTIN, or the company. */
export type BillToSource = 'PLANT' | 'PLANT_WITH_TENANT_GSTIN' | 'TENANT';

export interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  /** Every invoice bills exactly one plant of its company. */
  plantId: string;
  plant?: InvoicePlant;
  /** Who the invoice is addressed to — frozen at issue, never re-read. */
  billToName: string;
  billToGstin: string | null;
  billToAddress: string | null;
  billToEmail: string | null;
  billToContactName: string | null;
  billToContactPhone: string | null;
  billToPlantCode: string;
  billToPlantName: string;
  billToSource: BillToSource;
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotalPaise: number;
  taxTreatment: TaxTreatment;
  taxRateBasisPoints: number;
  taxPaise: number;
  totalPaise: number;
  issuedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  notes: string | null;
  lineCount?: number;
  /** Summed from the payment ledger, never stored on the invoice. */
  paidPaise?: number;
  outstandingPaise?: number;
}

/**
 * Money received against an invoice.
 *
 * Append-only: a wrong entry is corrected by a reversal that carries a
 * negative amount and points at what it reverses, so the ledger and the bank
 * statement can still be reconciled a year later.
 */
export interface InvoicePaymentRow {
  id: string;
  invoiceId: string;
  amountPaise: number;
  receivedOn: string;
  method: 'BANK_TRANSFER' | 'CHEQUE' | 'CASH' | 'UPI' | 'ADJUSTMENT';
  reference: string | null;
  notes: string | null;
  reversesId: string | null;
  reversalReason: string | null;
  createdAt: string;
  /** On the answer to recording a payment: where else the account already held its reference. */
  referenceUses?: PaymentReferenceUse[];
}

/**
 * One other place a company's account already carries a bank reference
 * (compared trimmed and case-insensitively). Reuse is reported, never
 * refused: one transfer can pay several invoices.
 */
export interface PaymentReferenceUse {
  /** A company's submission, or a payment Finance recorded straight onto the ledger. */
  kind: 'SUBMISSION' | 'PAYMENT';
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  plantId: string;
  plantCode: string;
  amountPaise: number;
  /** The payment date as claimed (submission) or as received (ledger). */
  date: string;
  status: PaymentSubmissionStatus | 'RECORDED' | 'REVERSED';
  /** Still stands: pending, confirmed, or on the ledger and not reversed. */
  live: boolean;
}

/** The answer to "is this reference already somewhere on the account?". */
export interface PaymentReferenceCheck {
  reference: string;
  uses: PaymentReferenceUse[];
  /** Live uses on plants the caller is not bound to — counted, not described. */
  elsewhere: number;
}

export const PAYMENT_METHOD_LABEL: Record<InvoicePaymentRow['method'], string> = {
  BANK_TRANSFER: 'Bank transfer',
  CHEQUE: 'Cheque',
  CASH: 'Cash',
  UPI: 'UPI',
  ADJUSTMENT: 'Adjustment',
};

/** What a company's account looks like, across every invoice raised for it. */
export interface BillingTotals {
  /** Issued invoices only — a draft has asked for nothing yet. */
  billedPaise: number;
  paidPaise: number;
  outstandingPaise: number;
  draftCount: number;
  issuedCount: number;
  /** Issued and not yet settled in full. */
  openCount?: number;
}

/** The invoice list page: rows, account totals, and the same totals per plant. */
export interface InvoicePage {
  items: InvoiceRow[];
  totals: BillingTotals;
  byPlant: Array<BillingTotals & { plant: InvoicePlant }>;
}

/** A recent entry in the payment ledger, with the invoice it was received against. */
export interface RecentPaymentRow {
  id: string;
  amountPaise: number;
  receivedOn: string;
  method: string;
  reference: string | null;
  reversesId: string | null;
  invoice: { id: string; invoiceNumber: string; currency: string; plant: InvoicePlant };
}

export interface InvoiceLineRow {
  id: string;
  /** EXAMINATION prices the visit; EXTRA_TEST one test prescribed outside the protocol. */
  kind?: 'EXAMINATION' | 'EXTRA_TEST';
  testId?: string | null;
  test?: { code: string; name: string } | null;
  description: string;
  /** What matched in the rate card, so a disputed line can be explained later. */
  rateBasis: string;
  quantity: number;
  unitPaise: number;
  amountPaise: number;
  servicedOn: string;
  order?: {
    examinationType: string;
    employee: { employeeCode: string; fullName: string };
  };
}

/** A plant as the billing screens list it, with its optional billing identity. */
export interface BillablePlant extends InvoicePlant {
  status: string;
  gstin: string | null;
  billingName: string | null;
  billingEmail: string | null;
}

export type PaymentSubmissionStatus = 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'CANCELLED';

/**
 * A company's "we have paid" (docs/37 A1, Step 6). A claim, not money: only
 * a CONFIRMED submission has a ledger payment behind it.
 */
export interface PaymentSubmissionRow {
  id: string;
  tenantId: string;
  plantId: string;
  invoiceId: string;
  amountPaise: number;
  paidOn: string;
  method: 'BANK_TRANSFER' | 'UPI' | 'CHEQUE';
  reference: string;
  notes: string | null;
  proofFileName: string;
  proofMimeType: string;
  proofSizeBytes: number;
  status: PaymentSubmissionStatus;
  submittedBy: string;
  submittedByName?: string | null;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedByName?: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  confirmedPaymentId: string | null;
  /** Where else the company's account carries this submission's reference. Empty when nowhere. */
  referenceUses?: PaymentReferenceUse[];
}

/** A row of Dr Ultimate's review queue. */
export interface PaymentSubmissionQueueRow extends PaymentSubmissionRow {
  tenant: { name: string };
  plant: InvoicePlant;
  invoice: { id: string; invoiceNumber: string; status: InvoiceStatus; totalPaise: number; currency: string };
  /** Confirmed outstanding on the invoice now. */
  outstandingPaise: number;
  /** Every pending submission on the invoice, this one included. */
  pendingPaise: number;
}

export interface InvoiceDetail extends InvoiceRow {
  /** Submitted and awaiting verification — not paid, not submittable again. */
  pendingSubmittedPaise?: number;
  /** Outstanding less what is already submitted. */
  availableToSubmitPaise?: number;
  rateCard: { name: string; effectiveFrom: string } | null;
  lines: InvoiceLineRow[];
}

/** ₹ from paise. Integers in, a string out — never a float in between. */
export function formatPaise(paise: number, currency = 'INR'): string {
  const sign = paise < 0 ? '-' : '';
  const abs = Math.abs(paise);
  const rupees = Math.floor(abs / 100);
  const p = String(abs % 100).padStart(2, '0');
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  // Indian grouping: the last three digits, then pairs.
  const s = String(rupees);
  const head = s.length > 3 ? s.slice(0, -3) : '';
  const tail = s.length > 3 ? s.slice(-3) : s;
  const grouped = head ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}` : tail;
  return `${sign}${symbol}${grouped}.${p}`;
}


// ── Roles and permissions ────────────────────────────────────────────

export type ScopeType = 'GLOBAL' | 'TENANT' | 'PLANT' | 'PARTNER' | 'CAMP' | 'SELF';

export const SCOPE_LABEL: Record<ScopeType, string> = {
  GLOBAL: 'Whole platform',
  TENANT: 'One company',
  PLANT: 'Named plants',
  PARTNER: 'One venue',
  CAMP: 'One camp',
  SELF: 'Own record only',
};

export interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  scopeType: ScopeType;
  /** Seeded (built-in) roles. Their key cannot change. */
  isSystem: boolean;
  /** Whether the role can be deleted once nobody holds it. */
  deletable: boolean;
  permissionCount: number;
  /** How many people actually hold it. */
  holders: number;
  permissions: string[];
}

export interface RoleDetail extends Omit<RoleRow, 'permissionCount'> {}

export interface PermissionEntry {
  key: string;
  description: string | null;
  /** Role keys holding it. */
  heldBy: string[];
  /** True when exercising it demands a second factor. */
  stepUp: boolean;
}

export interface PermissionDomain {
  domain: string;
  permissions: PermissionEntry[];
}


// ── Platform staff ───────────────────────────────────────────────────

export interface PlatformUserRow {
  id: string;
  email: string;
  fullName: string;
  status: UserStatus;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  suspendedReason: string | null;
  /** Sessions neither revoked nor expired. */
  liveSessions: number;
  roles: Array<{ key: string; name: string }>;
}

export interface PlatformUserDetail {
  id: string;
  email: string;
  fullName: string;
  status: UserStatus;
  mfaEnabled: boolean;
  mfaActivatedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  lockedUntil: string | null;
  failedLogins: number;
  suspendedAt: string | null;
  suspendedReason: string | null;
  roles: Array<{ key: string; name: string; scopeType: string; scopeIds: string[] }>;
  /** Everything the roles add up to, computed now rather than from a token. */
  permissions: string[];
  /** The subset that will demand a second factor. */
  stepUpPermissions: string[];
  /** Holds step-up acts but has no second factor set up. */
  mfaGap: boolean;
}

export interface SessionRow {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  ip: string | null;
  userAgent: string | null;
  live: boolean;
}


// ── The HR dashboard ───────────────────────────────────────────

/** One stage of the examination funnel, as the dashboard renders it. */
export interface PipelineStage {
  key: string;
  label: string;
  count: number;
}

export interface Pipeline {
  stages: PipelineStage[];
  /** Work that stopped rather than work in a stage — open no-shows. */
  stalled: number;
  /** In flight: every stage before the physician's signature, plus the open no-shows. */
  open: number;
  /** Signed by the physician. Finished, so not part of `open`. */
  certified: number;
  /** Cancelled, expired, closed no-shows. Reported so the stages reconcile. */
  closed: number;
  total: number;
  asOf: string;
}

/** What needs somebody to do something today. */
export interface DashboardAlerts {
  overdueExaminations: number;
  expiringCertificates: number;
  awaitingPhysicianReview: number;
  openNoShows: number;
  /** Booked for a day that has gone, nobody checked in, not yet a no-show. */
  passedWithoutCheckIn: number;
  retestsRequired: number;
  expiringWithinDays: number;
  asOf: string;
}

export interface UpcomingAppointment {
  id: string;
  scheduledFor: string;
  status: string;
  venueType: string;
  partner?: { name: string } | null;
  order?: {
    plantId: string;
    employee?: { id: string; employeeCode: string; fullName: string };
  };
}

/** One month of the dashboard trend: examinations raised by type, and signed off. */
export interface TrendMonth {
  /** `YYYY-MM`, India's calendar. */
  key: string;
  /** "Nov 25". */
  label: string;
  preEmployment: number;
  periodic: number;
  other: number;
  /** The three above, summed. */
  raised: number;
  certified: number;
  /** Treatment records (casualty, first aid, OPD) by the month the worker was seen. */
  treatments: number;
}

export interface TrendMixItem { key: string; label: string; count: number }

/**
 * The year behind the dashboard charts. `mix.unit` says what the donut
 * counts: a company's own examinations by type, or — on the platform
 * dashboard — companies by the type they mostly ran.
 */
export interface DashboardTrends {
  generatedAt: string;
  from: string;
  to: string;
  months: TrendMonth[];
  mix: { unit: 'examinations' | 'companies'; total: number; items: TrendMixItem[] };
}

/** A campaign as the "upcoming camps" table shows it. */
export interface UpcomingCamp {
  id: string;
  name: string;
  examinationType: string;
  status: CampaignStatus;
  startsOn: string;
  endsOn: string;
  /** The cohort frozen at launch; null for a draft. */
  planned: number | null;
  raised: number;
  booked: number;
  /** Platform dashboard only. */
  company?: { id: string; name: string };
}

/** A business event, read from the audit trail rather than a second feed. */
export interface ActivityRow {
  id: string;
  action: string;
  entityType: string;
  entityId?: string | null;
  actorId?: string | null;
  occurredAt: string;
  metadata?: Record<string, unknown> | null;
  /** Who or what the event concerns — a worker's name and code, a campaign's name. Null when the reader may not open that record. */
  subject?: string | null;
}

// ── The due list ───────────────────────────────────────────────

export type DueBucket =
  | 'OVERDUE' | 'DUE_TODAY' | 'DUE_THIS_WEEK' | 'DUE_THIS_MONTH' | 'EXPIRING_SOON';

export const DUE_BUCKET_LABEL: Record<DueBucket, string> = {
  OVERDUE: 'Overdue',
  DUE_TODAY: 'Due today',
  DUE_THIS_WEEK: 'Due this week',
  DUE_THIS_MONTH: 'Due this month',
  EXPIRING_SOON: 'Expiring soon',
};

export interface DueRow {
  employeeId: string;
  employeeCode: string;
  fullName: string;
  plantId: string;
  plant: string | null;
  department: string | null;
  jobRole: string | null;
  certificateNumber: string;
  dueOn: string;
  daysRemaining: number;
  bucket: DueBucket;
  /** An examination already under way — do not chase this one. */
  renewalInProgress: boolean;
}

export interface DueList {
  buckets: Record<DueBucket, number>;
  rows: DueRow[];
  withinDays: number;
  /** The cap was hit: the counts describe what was read, not the company. */
  truncated: boolean;
  asOf: string;
}

// ── Notifications ──────────────────────────────────────────────

export type InboxState = 'UNREAD' | 'READ' | 'ARCHIVED';

export interface InboxNotification {
  id: string;
  eventType: string;
  subject: string | null;
  body: string;
  linkPath: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  readAt: string | null;
  archivedAt: string | null;
}

// ── Industry, recommendation and a company's own configuration ────────
//
// Three layers that are deliberately not one. The INDUSTRY catalogue is what
// a chemical plant typically needs; a PACKAGE is the reusable group people
// compose with; a MEDICAL CONFIGURATION is what one company actually chose.
// Protocols are still what an examination runs — none of these replace that.

/** MANDATORY cannot be unticked; RECOMMENDED starts on; OPTIONAL starts off. */
export type InclusionLevel = 'MANDATORY' | 'RECOMMENDED' | 'OPTIONAL';

export const INCLUSION_LABEL: Record<InclusionLevel, string> = {
  MANDATORY: 'Required',
  RECOMMENDED: 'Recommended',
  OPTIONAL: 'Optional',
};

export interface Industry {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  companyCount: number;
  packageCount: number;
  hazardCount: number;
}

export interface IndustryDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  hazards: Array<{ id: string; code: string; name: string; isActive: boolean }>;
  packages: Array<{
    id: string; code: string; version: number; name: string;
    isActive: boolean; tier: InclusionLevel; sortOrder: number;
  }>;
}

export interface RecommendedPackage {
  id: string;
  code: string;
  version: number;
  name: string;
  category: string | null;
  tier: InclusionLevel;
  /** Why it was suggested. Somebody asked to accept it is owed the reason. */
  because: string;
  testCount: number;
}

export interface Recommendation {
  industry: { id: string; code: string; name: string } | null;
  hazards: Array<{ id: string; code: string; name: string }>;
  packages: RecommendedPackage[];
}

export interface ComposedTest {
  id: string;
  code: string;
  name: string;
  category: string;
  station: string | null;
  unit: string | null;
  valueType: string;
  inclusion: InclusionLevel;
  /** Which packages asked for it — two often ask for the same one. */
  fromPackages: string[];
  addedIndividually: boolean;
}

export interface ComposedSet {
  packages: Array<{ id: string; code: string; name: string }>;
  tests: ComposedTest[];
}

export type MedicalConfigStatus = 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';

export interface MedicalConfigurationRow {
  id: string;
  name: string;
  status: MedicalConfigStatus;
  configVersion: number;
  jobRoleId: string | null;
  jobRole: { id: string; code: string; title: string } | null;
  /** The department it is for, by code at every plant; null for a job role or the default. */
  departmentCode: string | null;
  departmentName: string | null;
  /** "Company default" when no job role — never a blank cell. */
  scope: string;
  effectiveFrom: string | null;
  publishedAt: string | null;
  packageCount: number;
  overrideCount: number;
}

export interface MedicalConfiguration {
  id: string;
  name: string;
  notes: string | null;
  status: MedicalConfigStatus;
  configVersion: number;
  jobRoleId: string | null;
  jobRole: { id: string; code: string; title: string } | null;
  departmentCode: string | null;
  departmentName: string | null;
  effectiveFrom: string | null;
  publishedAt: string | null;
  packages: Array<{
    id: string; code: string; version: number; name: string;
    category: string | null; sortOrder: number;
  }>;
  /** The adjustments, kept separately from the resolved set they produce. */
  added: Array<{ id: string; code: string; name: string; category: string }>;
  removed: Array<{ id: string; code: string; name: string; category: string }>;
  tests: ComposedTest[];
}

export interface InForceConfiguration {
  configuration: { id: string; name: string; configVersion: number } | null;
  appliedFrom: 'JOB_ROLE' | 'COMPANY_DEFAULT' | null;
  tests: ComposedTest[];
}


// ── Reports ────────────────────────────────────────────────────

export interface ReportDefinitionRow {
  key: string;
  label: string;
  description: string;
  formats: string[];
}

export type ReportJobStatus = 'PENDING' | 'RUNNING' | 'READY' | 'FAILED' | 'EXPIRED';

export interface ReportJobRow {
  id: string;
  reportType: string;
  format: string;
  status: ReportJobStatus;
  rowCount: number | null;
  byteSize: number | null;
  lastError: string | null;
  parameters: Record<string, unknown>;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
}

// ── Settings ───────────────────────────────────────────────────

export interface CompanyProfile extends Address, ContactDetails, CompanyExtras {
  id: string;
  name: string;
  legalName: string | null;
  gstin: string | null;
  industry: string | null;
  industryId: string | null;
  industryRef?: { id: string; code: string; name: string } | null;
  contactDesignation?: string | null;
  website?: string | null;
  employeeCodePrefix?: string | null;
  employeeCodeNextSeq?: number;
  workforceBand?: WorkforceBand | null;
  workforceCount?: number | null;
  /// What the company's HR may see of a worker's Health Check Report.
  healthReportAccess?: HealthReportAccess;
  setupMode?: SetupMode;
  setupStatus?: SetupStatus;
  setupCompletedAt?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// ── Onboarding ────────────────────────────────────────────────────

export type SetupStepKey =
  | 'profile' | 'location' | 'plants' | 'employees' | 'users' | 'medical' | 'review';

export interface SetupStep {
  key: SetupStepKey;
  index: number;
  total: number;
  title: string;
  hint: string;
  path: string;
  required: boolean;
  done: boolean;
  actuallyDone?: boolean;
  skipped: boolean;
  doneAt: string | null;
  detail: string;
  missing?: string[];
}

export interface OnboardingStatus {
  tenant: {
    id: string; name: string;
    legalName?: string | null;
    gstin?: string | null;
    industry: { id: string; code: string; name: string } | null;
    industryId?: string | null;
    contactName?: string | null;
    contactPhone?: string | null;
    contactEmail?: string | null;
    contactDesignation?: string | null;
    website?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    locality?: string | null;
    landmark?: string | null;
    city?: string | null;
    district?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    postOffice?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    placeId?: string | null;
    workforceBand: WorkforceBand | null; workforceCount: number | null;
    employeeCodePrefix: string | null;
  };
  setupMode: SetupMode;
  setupStatus: SetupStatus;
  recordedStatus: string;
  setupCompletedAt: string | null;
  progress: { completed: number; total: number };
  current: SetupStepKey;
  missingRequired: string[];
  counts: {
    plants: number; departments: number; employees: number; users: number;
    /** Company users invited and not signed in yet; `users` counts the active ones. */
    invitedUsers?: number;
    publishedConfigurations: number; draftConfigurations: number; openChangeRequests: number;
  };
  suggestion: { path: 'DONE' | 'MANUAL' | 'IMPORT' | 'ASSISTED'; text: string } | null;
  steps: SetupStep[];
}

export interface MedicalConfigChangeRequest {
  id: string;
  subject: string;
  message: string;
  status: 'OPEN' | 'IN_REVIEW' | 'RESOLVED' | 'REJECTED';
  response: string | null;
  requestedBy: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  config: { id: string; name: string; status: string } | null;
}

export interface MedicalConfigOverview {
  industry: { id: string | null; code: string | null; name: string } | null;
  setupMode: SetupMode;
  setupStatus: SetupStatus;
  recommendation: Recommendation | null;
  configurations: MedicalConfigurationRow[];
  hasCompanyDefault: boolean;
  jobRoles: Array<{
    id: string; code: string; title: string; employees: number;
    hazards: Array<{ id: string; code: string; name: string }>;
    coverage: 'OWN' | 'COMPANY_DEFAULT' | 'NONE';
  }>;
  openRequests: Array<{ id: string; subject: string; status: string; createdAt: string }>;
}

export interface ExaminationReadiness {
  /** The type the raise dialog starts on: candidate, configuration name, or a recent joiner. */
  suggestedType?: string;
  /** The package this examination type runs for the company: the default, or one HR chose. */
  package?: {
    id: string; code: string; name: string; version: number;
    defaultFor: 'PRE_EMPLOYMENT' | 'YEARLY' | null; tenantId: string | null; source: 'DEFAULT' | 'HR';
  } | null;
  employee: {
    id: string; employeeCode: string; fullName: string; status: string;
    plant: { id: string; code: string; name: string } | null;
    department: { id: string; name: string } | null;
  };
  openOrder: { id: string; status: string; examinationType: string; createdAt: string } | null;
  jobRole: { id: string; code: string; title: string } | null;
  hazards: Array<{ id: string; code: string; name: string }>;
  protocols: Array<{ id: string; code: string; version: number; name: string; periodicityMonths: number }>;
  tests: Array<{ id: string; code: string; name: string; category: string; isMandatory: boolean }>;
  periodicityMonths: number | null;
  requiresConsent: boolean;
  /** PROTOCOL: from the job role's hazards. MEDICAL_PACKAGE: from the company's medical package. */
  source?: 'PROTOCOL' | 'MEDICAL_PACKAGE' | null;
  configuration: { id: string; name: string; configVersion: number; appliedFrom: string | null; testCount: number } | null;
  ready: boolean;
  missing: Array<{ field: string; code: string; message: string }>;
  blockers: Array<{ code: string; message: string; orderId?: string }>;
  /** Worth knowing before raising — a recent case of the same kind, a certificate still valid. Never a refusal. */
  notices?: Array<{ code: string; message: string; orderId?: string }>;
}

export interface RecommendationRule {
  id: string;
  name: string;
  industryId: string | null;
  hazardId: string | null;
  examinationType: string | null;
  packageId: string;
  tier: InclusionLevel;
  priority: number;
  isActive: boolean;
  updatedAt: string;
  industry: { id: string; code: string; name: string } | null;
  hazard: { id: string; code: string; name: string } | null;
  package: { id: string; code: string; name: string; version: number };
}

export interface PartnerLocation extends Address {
  id: string;
  partnerId: string;
  code: string;
  name: string;
  isPrimary: boolean;
  status: string;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  emergencyPhone?: string | null;
  operatingDays?: string | null;
  operatingHours?: string | null;
  dailyCapacity?: number | null;
  services?: string | null;
  notes?: string | null;
  appointments?: number;
}

export interface CampaignVenue extends Address {
  id: string;
  campaignId: string;
  name: string;
  plantId: string | null;
  partnerId: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  startsOn: string | null;
  endsOn: string | null;
  notes?: string | null;
  sortOrder: number;
  appointments?: number;
}

export interface SmtpSettings {
  configured: boolean;
  source: 'DATABASE' | 'ENVIRONMENT' | 'MIXED' | null;
  fields: Record<'host' | 'port' | 'user' | 'secure' | 'fromName' | 'fromEmail' | 'replyTo',
    { value: string | null; fromEnv: boolean; set: boolean }>;
  passwordSet: boolean;
  passwordFromEnv: boolean;
}

/** File storage (Tigris / S3-compatible bucket). The secret key is never returned. */
export interface StorageSettings {
  configured: boolean;
  source: 'DATABASE' | 'ENVIRONMENT' | 'MIXED' | null;
  bucket: string | null;
  endpoint: string | null;
  fields: Record<'accessKeyId' | 'bucket' | 'endpoint' | 'region',
    { value: string | null; fromEnv: boolean; set: boolean }>;
  secretSet: boolean;
  secretFromEnv: boolean;
}

export interface SupportSettings {
  fields: Record<'email' | 'phone' | 'hours' | 'consoleUrl' | 'employeeUrl',
    { value: string | null; fromEnv: boolean; set: boolean }>;
  effective: { email: string; phone: string | null; hours: string | null; consoleUrl: string | null; employeeUrl: string | null };
}

export interface TemplateSetting {
  event: string;
  default: { subject: string; body: string };
  override: { subject: string | null; body: string | null };
  customised: boolean;
}

export interface NotificationPreferences {
  emailEnabled: boolean;
  smsEnabled: boolean;
  quietFrom: number | null;
  quietUntil: number | null;
  updatedAt: string | null;
}

export interface SettingsPayload {
  company: CompanyProfile;
  /** Signed agreements on the company's file, read-only for HR; kept by Dr Ultimate. */
  agreements?: CompanyAgreementRow[];
  preferences: NotificationPreferences;
  structure: {
    plants: number;
    departments: number;
    designations: number;
    contractors: number;
  };
  /** Dr Ultimate's support contact, for the read-only cards that say "contact support". */
  support?: { email: string; phone: string | null; hours: string | null } | null;
}

// ────────────────────────────────────────────────────────────
// STAFF PAYOUT
// ────────────────────────────────────────────────────────────

/**
 * The two events that earn.
 *
 * Only the final billable sign-off. Station technicians perform real work and
 * are deliberately not paid through this ledger — that would be a different
 * business rule needing its own rate line.
 */
/**
 * The one staff payout event. A physician's sign-off is NOT here — physicians
 * are paid through `Settlement`, which prices the same review as REPORT_REVIEW.
 */
export type PayoutEvent = 'RESULT_RELEASE';

export const PAYOUT_EVENT_LABEL: Record<PayoutEvent, string> = {
  RESULT_RELEASE: 'Lab result released',
};

export interface EarningLine {
  event: PayoutEvent;
  count: number;
  /** Null when more than one rate applied across the period. */
  ratePaise: number | null;
  amountPaise: number;
  /** Events the period covers that no published rate card priced. */
  unpricedCount: number;
}

export interface StaffEarnings {
  staffUserId: string;
  fullName: string | null;
  email: string | null;
  lines: EarningLine[];
  earnedPaise: number;
  paidPaise: number;
  /** Earned less paid. Can be negative — an advance, or a late reversal. */
  pendingPaise: number;
  unpricedCount: number;
}

export interface PayoutRateLineRow {
  id: string;
  event: PayoutEvent;
  label: string;
  amountPaise: number;
}

export interface PayoutRateCardRow {
  id: string;
  name: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
  publishedAt: string | null;
  notes: string | null;
  lines: PayoutRateLineRow[];
}

export interface StaffPaymentRow {
  id: string;
  amountPaise: number;
  paidOn: string;
  notes: string | null;
  /** Set when this row IS a reversal of another. */
  reversesId: string | null;
  reversalReason: string | null;
  createdAt: string;
  /** Set when this row HAS BEEN reversed by another. */
  reversedBy: { id: string; createdAt: string } | null;
}

// ────────────────────────────────────────────────────────────
// EMPLOYEE DOCUMENTS
// ────────────────────────────────────────────────────────────

export type DocumentCategory =
  | 'CASUALTY' | 'EXTERNAL_MEDICAL' | 'EXTERNAL_FITNESS'
  | 'CONSENT_SCAN' | 'IDENTITY' | 'OTHER';

export const DOCUMENT_CATEGORY_LABEL: Record<DocumentCategory, string> = {
  CASUALTY: 'Casualty / incident',
  EXTERNAL_MEDICAL: 'External medical record',
  EXTERNAL_FITNESS: 'External fitness certificate',
  CONSENT_SCAN: 'Signed consent (scan)',
  IDENTITY: 'Identity proof',
  OTHER: 'Other',
};

/** What each category is for, shown where somebody has to pick one. */
export const DOCUMENT_CATEGORY_HINT: Record<DocumentCategory, string> = {
  CASUALTY: 'Paperwork about an injury or emergency at work.',
  EXTERNAL_MEDICAL: 'Records from a hospital or a previous employer.',
  EXTERNAL_FITNESS: 'A fitness certificate this platform did not issue.',
  CONSENT_SCAN: 'Paper consent signed at a camp or reception.',
  IDENTITY: 'ID card or proof of identity.',
  OTHER: 'Anything else that belongs on this record.',
};

export interface EmployeeDocumentRow {
  id: string;
  category: DocumentCategory;
  title: string;
  occurredOn: string | null;
  notes: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string;
}

/** Bytes as a person reads them. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ────────────────────────────────────────────────────────────
// TREATMENT RECORDS
// ────────────────────────────────────────────────────────────

/** A dated casualty, first-aid or OPD treatment given to a worker on the premises. */
export type TreatmentKind = 'CASUALTY' | 'FIRST_AID' | 'OPD';
export type TreatmentOutcome = 'RETURNED_TO_WORK' | 'SENT_HOME' | 'REFERRED' | 'HOSPITALISED';

export const TREATMENT_KINDS: TreatmentKind[] = ['CASUALTY', 'FIRST_AID', 'OPD'];
export const TREATMENT_OUTCOMES: TreatmentOutcome[] = ['RETURNED_TO_WORK', 'SENT_HOME', 'REFERRED', 'HOSPITALISED'];

export const TREATMENT_KIND_LABEL: Record<TreatmentKind, string> = {
  CASUALTY: 'Casualty',
  FIRST_AID: 'First aid',
  OPD: 'OPD visit',
};

/** What each kind is for, shown where somebody has to pick one. */
export const TREATMENT_KIND_HINT: Record<TreatmentKind, string> = {
  CASUALTY: 'An injury or emergency on the premises.',
  FIRST_AID: 'Minor treatment at the first-aid post — a cut, a burn, a sprain.',
  OPD: 'An out-patient consultation at the occupational health centre.',
};

export const TREATMENT_OUTCOME_LABEL: Record<TreatmentOutcome, string> = {
  RETURNED_TO_WORK: 'Returned to work',
  SENT_HOME: 'Sent home',
  REFERRED: 'Referred',
  HOSPITALISED: 'Hospitalised',
};

/** The outcomes that name a destination. */
export const TREATMENT_REFERRAL_OUTCOMES: TreatmentOutcome[] = ['REFERRED', 'HOSPITALISED'];

export interface TreatmentAttachmentRow {
  documentId: string;
  title: string;
  category: DocumentCategory;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  attachedAt: string;
}

export interface TreatmentRow {
  id: string;
  employeeId: string;
  employee: {
    id: string; fullName: string; employeeCode: string; plantId?: string | null; status: string;
  };
  kind: TreatmentKind;
  occurredAt: string;
  location: string | null;
  complaint: string;
  treatmentGiven: string;
  outcome: TreatmentOutcome;
  referredTo: string | null;
  workRelated: boolean;
  /** YYYY-MM-DD. */
  followUpDate: string | null;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt: string;
  attachmentCount: number;
  /** Present on a single record; the list carries the count alone. */
  attachments?: TreatmentAttachmentRow[];
}

export interface TreatmentSummary {
  total: number;
  byKind: Record<TreatmentKind, number>;
  workRelated: number;
  /** Follow-ups due today or later. */
  followUpsPending: number;
}

/**
 * A sample's state and tube type, for people to read.
 *
 * Every lab screen rendered these with `replace(/_/g, ' ').toLowerCase()`,
 * which is how "resulted" and "blood edta" reached the bench. Unknown values
 * fall back to the raw key at the call site rather than to nothing.
 */
export const SAMPLE_STATUS_LABEL: Record<string, string> = {
  ORDERED: 'Ordered',
  COLLECTED: 'Collected',
  IN_TRANSIT: 'In transit',
  RECEIVED: 'Received',
  PROCESSING: 'Processing',
  RESULTED: 'Resulted',
  VALIDATED: 'Validated',
  RELEASED: 'Released',
  REJECTED: 'Rejected',
  RECOLLECT_REQUESTED: 'Recollection requested',
};

export type SampleType = 'NONE' | 'BLOOD_EDTA' | 'BLOOD_SERUM' | 'BLOOD_FLUORIDE' | 'URINE' | 'SPUTUM';

export const SAMPLE_TYPE_LABEL: Record<string, string> = {
  NONE: 'Not drawn — captured at a station',
  BLOOD_EDTA: 'Blood (EDTA)',
  BLOOD_SERUM: 'Blood (serum)',
  BLOOD_FLUORIDE: 'Blood (fluoride)',
  URINE: 'Urine',
  SPUTUM: 'Sputum',
};

/** An examination's type, for people to read. Screens used to lowercase the key into "periodic ohc". */
export const EXAM_TYPE_LABEL: Record<string, string> = {
  PRE_EMPLOYMENT: 'Pre-employment',
  PERIODIC_OHC: 'Periodic (OHC)',
  SPECIAL: 'Special',
  RETURN_TO_WORK: 'Return to work',
  POST_INCIDENT: 'Post-incident',
  EXIT: 'Exit',
  RETEST: 'Retest',
  ANNUAL_HEALTH_CHECKUP: 'Annual health check-up',
  REVIEW_CORRECTION: 'Correction re-review',
};

/** A venue's type, for people to read. "both" was not a type anybody would write. */
export const PARTNER_TYPE_LABEL: Record<string, string> = {
  HOSPITAL: 'Hospital',
  LABORATORY: 'Laboratory',
  BOTH: 'Hospital and laboratory',
};

// ── Promotional banners (parity item P3) ─────────────────────────────

/** Where a banner is in its life, as the admin list shows and filters it. */
export type PromotionStatus = 'LIVE' | 'SCHEDULED' | 'EXPIRED' | 'INACTIVE';

/** A banner as the publishing side sees it. */
export interface PromotionalAdRow {
  id: string;
  /** null: addressed to every company. */
  tenantId: string | null;
  tenantName: string | null;
  title: string;
  actionUrl: string | null;
  imageMimeType: string;
  /** Relative to the API base; the client prefixes it. Carries a cache-busting version. */
  imagePath: string;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  status: PromotionStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

/** A banner as a worker's home screen receives it: nothing about who published it. */
export interface EmployeePromotion {
  id: string;
  title: string;
  actionUrl: string | null;
  imagePath: string;
}

// ────────────────────────────────────────────────────────────
// EMERGENCY VISITS AND BILLS (Requirement 2)
// ────────────────────────────────────────────────────────────

/** DRAFT at the venue → AWAITING_CONFIRMATION with Dr Ultimate → SUBMITTED to the company. */
export type EmergencyVisitStatus = 'DRAFT' | 'AWAITING_CONFIRMATION' | 'SUBMITTED';
/** Where the bill stands with the company. Never "approved" or "paid". */
export type EmergencyBillStatus = 'SUBMITTED' | 'REVIEWED' | 'QUERIED';
export type EmergencySummarySource = 'AI' | 'AI_EDITED' | 'MANUAL';
/** How much clinical text the company's policy lets this reader see. */
export type EmergencyClinicalAccess = 'FULL' | 'SUMMARY' | 'NONE';

export const EMERGENCY_BILL_STATUS_LABEL: Record<EmergencyBillStatus, string> = {
  SUBMITTED: 'Awaiting review',
  REVIEWED: 'Reviewed',
  QUERIED: 'Queried',
};
export const EMERGENCY_SUMMARY_SOURCE_LABEL: Record<EmergencySummarySource, string> = {
  AI: 'AI draft, confirmed by Dr Ultimate',
  AI_EDITED: 'AI draft, edited and confirmed by Dr Ultimate',
  MANUAL: 'Written by Dr Ultimate from the notes',
};

export interface EmergencySummaryFields {
  presentingIssue: string | null;
  examinationFindings: string | null;
  treatmentGiven: string | null;
}
export interface EmergencyAiDraft extends EmergencySummaryFields {
  /** What the notes leave unclear, for the clinician to resolve. */
  ambiguities: string[];
}

/** A company that arranged emergency care at the caller's venue. */
export interface EmergencyCompany { tenantId: string; name: string; partnerId: string; partnerName: string }

/** Just enough to confirm the right person is at the desk. */
export interface EmergencyEmployeeMatch {
  tenantId: string;
  company: string;
  employeeId: string;
  fullName: string;
  employeeCode: string;
  sex: string | null;
  birthYear: number | null;
  plant: string | null;
  photo: { url: string; expiresAt: string } | null;
}

export interface EmergencyVisitVenue extends EmergencySummaryFields {
  id: string;
  status: EmergencyVisitStatus;
  company: { id: string; name: string };
  partner: { id: string; name: string };
  employee: { id: string; fullName: string; employeeCode: string };
  /** YYYY-MM-DD. The treatment date. */
  visitDate: string;
  rawNotes: string;
  clinicianName: string | null;
  aiDraft: EmergencyAiDraft | null;
  aiModel: string | null;
  aiGeneratedAt: string | null;
  /** The notes changed after the AI draft was made. */
  aiStale: boolean;
  summarySource: EmergencySummarySource | null;
  summaryConfirmedAt: string | null;
  summaryConfirmedByName: string | null;
  billingReason: string | null;
  invoiceNumber: string | null;
  billedAmountPaise: number | null;
  invoice: { fileName: string; mimeType: string; sizeBytes: number; uploadedAt: string } | null;
  /** Sent to Dr Ultimate. */
  sentAt: string | null;
  /** Dr Ultimate sent it back, and why. */
  returnedAt: string | null;
  returnNote: string | null;
  submittedAt: string | null;
  billStatus: EmergencyBillStatus | null;
  reviewNote: string | null;
  /** What still stands between this draft and sending it. Empty when ready. */
  missing: string[];
  createdAt: string;
}

export interface EmergencyClinicalView {
  presentingIssue: string | null;
  treatmentGiven: string | null;
  /** FULL only. */
  examinationFindings?: string | null;
  /** FULL only: the doctor's original notes. */
  rawNotes?: string;
  summarySource: EmergencySummarySource | null;
  confirmedByName: string | null;
  aiModel: string | null;
}

export interface EmergencyBillRow {
  id: string;
  employee: { id: string; fullName: string; employeeCode: string; plant: string | null };
  partner: { id: string; name: string };
  visitDate: string;
  billingReason: string | null;
  invoiceNumber: string;
  billedAmountPaise: number;
  billStatus: EmergencyBillStatus;
  submittedAt: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  hasInvoice: boolean;
  /** Null when the company's policy, or the reader's role, withholds it. */
  clinical: EmergencyClinicalView | null;
}

export interface EmergencyBillPage {
  items: EmergencyBillRow[];
  nextCursor: string | null;
  clinicalAccess: EmergencyClinicalAccess;
}

export interface EmergencyArrangementRow {
  id: string;
  partner: { id: string; name: string; code: string; city: string | null };
  note: string | null;
  createdAt: string;
}

/** A visit as Dr Ultimate's administrator reviews it. */
export interface EmergencyVisitAdmin extends EmergencySummaryFields {
  id: string;
  tenantId: string;
  status: EmergencyVisitStatus;
  company: { id: string; name: string };
  partner: { id: string; name: string; code: string };
  employee: { id: string; fullName: string; employeeCode: string; sex: string | null; birthYear: number | null; plant: string | null };
  visitDate: string;
  /** Absent in the queue listing. */
  rawNotes?: string;
  clinicianName: string | null;
  aiDraft?: EmergencyAiDraft | null;
  aiModel: string | null;
  aiGeneratedAt: string | null;
  aiStale: boolean;
  summarySource: EmergencySummarySource | null;
  summaryConfirmedAt: string | null;
  summaryConfirmedByName: string | null;
  billingReason: string | null;
  invoiceNumber: string | null;
  billedAmountPaise: number | null;
  invoice: { fileName: string; mimeType: string; sizeBytes: number; uploadedAt: string } | null;
  sentAt: string | null;
  submittedAt: string | null;
  billStatus: EmergencyBillStatus | null;
}

export interface EmergencyAdminArrangementRow {
  id: string;
  tenantId: string;
  note: string | null;
  createdAt: string;
  partner?: { id: string; name: string; code: string; city: string | null };
  company?: { id: string; name: string };
}
